import { v4 as uuid } from "uuid";
import md5 from "blueimp-md5";
import {
    assert,
    assertNever,
    DefaultMap,
    defined,
    definedMap,
    mapFilterUndefined,
    panic,
} from "@glideapps/ts-necessities";
import zip from "lodash/zip";
import shuffle from "lodash/shuffle";

import { PrimitiveValue } from "common/components/computation-types";
import { Data } from "common/components/data";
import { RootPath, isRootPath, RelativePath, isTopLevelPath, isKeyPath } from "common/computation-model/paths";
import { RowIndex } from "common/Database";
import { TableName } from "common/database-strings";
import { BaseRowIndex, isBaseRowIndex } from "common/firebase-function-types";
import { handlerForArrayScreenFormat } from "common/generator/array-screens";
import { Appearance } from "common/generator/components/button-appearance";
import { getColumnAssignments, makeInputOutputTablesForClassOrArrayScreen } from "common/generator/description-utils";
import { rowIndexColumnName } from "common/google-sheets";
import { makeRowID } from "common/make-row-id";
import { filterType, findType, isEmptyOrUndefined, nullToUndefined } from "common/Support";
import { ChangeObservable, Watchable } from "common/change-obserable";
import { AppDescription, getAppTabs, ScreenFlag } from "common/components/SerializedApp";
import {
    ActionSource,
    AppEnvironment,
    AppFacilities,
    AppOther,
    ComputationModel,
    DataStore,
    UserProfileTableInfo,
} from "common/components/types";
import {
    GroundValue,
    Row,
    Table,
    asMaybeString,
    follow,
    getRowColumn,
    asTable,
    asRow,
} from "common/computation-model/data";
import {
    ExistingAppDescriptionContext,
    InternalScreenContext,
    WireActionBackend,
    WireActionRunner,
    WireHydrationBackend,
    WireRowHydrationValueProvider,
    WireInflationBackend,
    WireRowComponentHydrationBackend,
    WireTableComponentHydrationBackend,
    WireTableComponentHydrator,
    WireValueGetter,
    WireRowComponentInflationBackend,
    WireTableGetter,
    PageScreenTarget,
    PageScreenPosition,
    WireTableTransformValueProvider,
    WirePredicate,
    ContextTableTypes,
    WireRowValueProviderMaker,
    WireActionCallbacks,
    InternalRowContext,
    WireComponentHydratorWithID,
    WireRowActionHydrationValueProvider,
} from "common/generator/components";
import {
    getEnumProperty,
    getNumberProperty,
    getScreenProperty,
    getSourceColumnProperty,
    getSpecialValueProperty,
    getStringProperty,
    getSwitchProperty,
    getTableProperty,
    makeSpecialValueProperty,
} from "common/generator/components/property-utils";
import {
    ArrayScreenDescription,
    ClassScreenDescription,
    InputOutputTables,
    LegacyPropertyDescription,
    ScreenDescriptionKind,
    TableGlideType,
    TableRefGlideType,
    TypeSchema,
    classScreenName,
    editClassScreenName,
    getScreenComponents,
    getTableColumn,
    getTableName,
    getTableRefTableName,
    isComputedColumn,
    isEditClassScreenName,
    isFormScreen,
    isPrimitiveType,
    isSingleRelationType,
    makeInputOutputTables,
    areTableNamesEqual,
    isMultiRelationType,
    isAddClassScreenName,
    ScreenDescription,
    TabDescription,
    MutatingScreenKind,
    userProfileScreenName,
    ColumnType,
    ArrayTransform,
    ArrayTransformKind,
    TableOrderArrayTransform,
    ShuffleArrayTransform,
    LimitArrayTransform,
    SourceColumn,
    getSourceColumnPath,
    SourceColumnKind,
    SortArrayTransform,
    SortOrder,
    FilterArrayTransform,
    Formula,
    SpecialValueKind,
    isClassOrArrayScreenDescription,
    isAddOrEditOrFormScreenDescription,
    isStringTypeKind,
} from "common/generator/description";
import { inflateActions, inflateColumnAssignments, inflateComponent, registerActionRunner } from "common/wire/utils";
import {
    WireAction,
    WireComponent,
    WireEditableValue,
    WireNavigationModel,
    WireScreen,
    WireTab,
    WireUserProfile,
} from "common/wire";
import { MutableNamespace } from "common/computation-model/types";
import { RowSubscriptionHandler, SubscriptionHandler, TableSubscriptionHandler } from "./subscription";
import { ArraySet } from "common/support/array-set";
import { logInfo } from "common/debug-print";
import { getMutatingKindForScreen } from "lib/mutating-screen-kind";
import { getLocalizedString } from "common/components/localization";
import { WireComponentKind } from "common/wire/component-kind";
import { BuilderAction, BuilderActionsForApp } from "common/Database/builder-actions";
import {
    decomposePredicateCombinationFormula,
    decomposeSortKey,
} from "common/generator/formulas/formula-specifications";
import { ConditionValuePreparer, prepareCondition } from "common/computation-model/prepare-condition";
import { computeCondition } from "common/computation-model/conditions";
import { parseValueAsDateSync } from "common/components/component-helpers";
import { compareValues } from "common/components/primitives";
import fromPairs from "lodash/fromPairs";
import { ComponentKindUserProfile, UserProfileComponentDescription } from "common/generator/components/user-profile";
import { WireBackend, BuilderNavigationModel, NavigationModelData } from "./types";
import { getEditOnSubmitActions } from "common/generator/form-on-submit";
import { getUserProfileTableInfo } from "common/components/user-profile";

type ActionTokenMap = Map<string, [WireActionRunner, InternalRowContext]>;

type OnChangeData = ColumnOnChangeData;
type OnChangeDataTokenMap = Map<string, OnChangeData>;

interface SubscriptionHandlerAndPath {
    readonly path: RootPath;
    readonly handler: SubscriptionHandler;
}

interface SubscriptionInfo {
    readonly handlerAndPath: SubscriptionHandlerAndPath | undefined;
    readonly tokens: ReadonlySet<string>;

    readonly subsidiary: SubscriptionInfo | undefined;
}

// name -> [value, change token, convert function]
type StateValuesMap = Map<string, [unknown, string, (v: unknown) => unknown]>;

function makeStateSaveKey(context: ScreenHydrationContext, componentID: string | undefined): string | undefined {
    if (componentID === undefined) return undefined;
    return `${context.position}-${context.depth}-${componentID}`;
}

interface ComponentState extends SubscriptionInfo {
    readonly stateValues: StateValuesMap;
    readonly stateSaveKey: string | undefined;
    stateChanged: boolean;
}

function isSubscriptionDirty(si: SubscriptionInfo): boolean {
    if (si.handlerAndPath?.handler.needsRedraw === true) return true;
    if (si.subsidiary !== undefined) {
        return isSubscriptionDirty(si.subsidiary);
    }
    return false;
}

function isComponentStateDirty(cs: ComponentState): boolean {
    return isSubscriptionDirty(cs) || cs.stateChanged;
}

interface HydratedScreen {
    readonly wireScreen: WireScreen;

    readonly screenContextSubscriptionInfo: SubscriptionInfo | undefined;
    // These are kept in sync with `wireScreen.components`
    readonly componentStates: readonly ComponentState[];
    readonly titleSubscriptionInfo: SubscriptionInfo;

    readonly actions: ActionTokenMap;
    readonly onChangeData: OnChangeDataTokenMap;
}

interface InternalScreen {
    readonly screenName: string;
    readonly defaultTitle: string | undefined;
    readonly context: InternalScreenContext;
    readonly hydratedScreen: HydratedScreen | undefined;
}

interface ColumnOnChangeData {
    readonly kind: "column";
    readonly tableName: TableName;
    readonly columnName: string;
    // Right now we're assuming that if the row is visible, the data has to be
    // written to the data store.  If it's invisible, it's only modified
    // locally.
    readonly row: Row;
}

interface InternalTab {
    readonly isVisible: boolean;
    readonly visibilityPredicate: WirePredicate;
    readonly visibilitySubscriptionInfo: SubscriptionInfo;
    // This is a copy of screen name in the tab description, for easier access
    readonly tabScreenName: string;
}

interface InternalNavigationModel {
    readonly screen: InternalScreen;
    readonly sideScreen: InternalScreen | undefined;
    readonly modal: InternalScreen | undefined;

    readonly tabs: readonly InternalTab[] | undefined;

    readonly userProfile: WireUserProfile | undefined;
    readonly userProfileSubscriptionInfo: SubscriptionInfo | undefined;
}

const emptyScreen: WireScreen = { title: "", components: [], closeAction: undefined };

function areScreenContextsEqual(a: InternalScreenContext, b: InternalScreenContext): boolean {
    if (a.inputRow !== b.inputRow) return false;
    if (a.outputRow !== b.outputRow) return false;
    return true;
}

function keepSubscriptionInfo<T extends SubscriptionInfo>(
    hydrationContext: ScreenHydrationContext,
    hydratedScreen: HydratedScreen,
    si: T
): T {
    assert(hydratedScreen !== undefined);

    for (const token of si.tokens) {
        if (hydratedScreen.actions.has(token)) {
            hydrationContext.actions.set(token, defined(hydratedScreen.actions.get(token)));
        } else if (hydratedScreen.onChangeData.has(token)) {
            hydrationContext.onChangeValueRows.set(token, defined(hydratedScreen.onChangeData.get(token)));
        } else {
            return panic("Token not registered");
        }
    }

    if (si.subsidiary !== undefined) {
        keepSubscriptionInfo(hydrationContext, hydratedScreen, si.subsidiary);
    }

    return si;
}

// Should we return `undefined` instead of this getter?
const emptyValueGetter: WireValueGetter = () => undefined;
const emptyColumnGetter = [emptyValueGetter, { kind: "string" }] as const;

const emptyInternalScreenContext: InternalRowContext = {
    inputRow: undefined,
    outputRow: undefined,
    containingScreenRow: undefined,
};

type TableTransformer = (rvpm: WireRowValueProviderMaker, table: Table) => Row[];

class InflationBackend implements WireRowComponentInflationBackend {
    constructor(
        public readonly appFacilities: AppFacilities,
        private readonly context: ExistingAppDescriptionContext,
        public readonly tables: ContextTableTypes,
        private readonly computationModel: ComputationModel,
        public readonly mutatingScreenKind: MutatingScreenKind | undefined
    ) {}

    public get schema(): TypeSchema {
        return this.context.schema;
    }

    public get userProfileTableInfo(): UserProfileTableInfo | undefined {
        return this.context.userProfileTableInfo;
    }

    public findTable(ref: TableName | TableRefGlideType | undefined): TableGlideType | undefined {
        return this.context.findTable(ref);
    }

    public get appID(): string {
        return this.context.appID;
    }

    public get appDescription(): AppDescription {
        return this.context.appDescription;
    }

    public get builderActions(): BuilderActionsForApp {
        return this.context.builderActions;
    }

    public getBuilderAction(id: string): BuilderAction | undefined {
        return this.context.getBuilderAction(id);
    }

    private makeColumnGetter(
        sourceColumn: SourceColumn,
        inOutputRow: boolean,
        withFormat: boolean,
        tables: ContextTableTypes = this.tables
    ): readonly [WireValueGetter, ColumnType] {
        const sourceColumnPath = getSourceColumnPath(sourceColumn);
        assert(sourceColumnPath.length === 1);
        const columnName = sourceColumnPath[0];

        let table: TableGlideType;
        let rowGetter: (hb: WireRowHydrationValueProvider) => Row | undefined;

        if (sourceColumn.kind === SourceColumnKind.DefaultContext) {
            table = inOutputRow ? tables.output : tables.input;

            rowGetter = hb => {
                const sc = hb.rowContext;
                return inOutputRow ? sc?.outputRow ?? sc?.inputRow : sc?.inputRow;
            };
        } else if (sourceColumn.kind === SourceColumnKind.UserProfile) {
            assert(!inOutputRow);

            const info = this.userProfileTableInfo;
            if (info === undefined) return emptyColumnGetter;

            const tableName = info.tableName;
            const maybeTable = this.findTable(tableName);
            if (maybeTable === undefined) return emptyColumnGetter;
            table = maybeTable;

            const userProfilePath = this.computationModel.getUserProfileRowPath();
            if (userProfilePath === undefined) return emptyColumnGetter;

            rowGetter = hb => definedMap(hb.getGlobalValue(tableName, userProfilePath), asRow);
        } else if (sourceColumn.kind === SourceColumnKind.ContainingScreen) {
            if (tables.containingScreen === undefined) return emptyColumnGetter;
            table = tables.containingScreen;

            rowGetter = hb => hb.rowContext?.containingScreenRow;
        } else {
            return assertNever(sourceColumn.kind);
        }

        const column = getTableColumn(table, columnName);

        const tableName = getTableName(table);
        const paths = this.computationModel.getColumnPaths(tableName)?.get(columnName);

        if (column === undefined || paths === undefined) return emptyColumnGetter;

        const [[valuePath, valueRootPath], [formatPath, formatRootPath]] = paths;
        const path = withFormat ? formatPath ?? valuePath : valuePath;
        const rootPath = withFormat ? formatRootPath ?? valueRootPath : valueRootPath;

        let getter: WireValueGetter;
        if (isRootPath(path)) {
            getter = hb => hb.getGlobalValue(tableName, path);
        } else {
            getter = hb => {
                if (rootPath === undefined) return undefined;

                const row = rowGetter(hb);
                if (row === undefined) return undefined;

                return hb.getColumnInRow(tableName, rootPath, row.$rowID, path);
            };
        }
        return [getter, column.type];
    }

    public getValueGetterForColumn(
        sourceColumn: SourceColumn,
        inOutputRow: boolean,
        withFormat: boolean
    ): readonly [WireValueGetter, ColumnType] {
        return this.makeColumnGetter(sourceColumn, inOutputRow, withFormat);
    }

    private makeTableGetter(
        tableName: TableName
    ): ((hb: WireRowHydrationValueProvider) => Table | undefined) | undefined {
        const table = this.context.findTable(tableName);
        if (table === undefined) return undefined;

        const rootPath = this.computationModel.getBasePathForTable(tableName);
        if (rootPath === undefined) return undefined;

        return hb => definedMap(hb.getGlobalValue(tableName, rootPath), t => asTable(t));
    }

    // TODO: This should be in the description handlers for the most part.  Maybe.
    public getValueGetterForProperty(
        desc: LegacyPropertyDescription | undefined,
        withFormat: boolean,
        opts?: {
            inOutputRow?: boolean;
            columnFirst?: boolean;
        }
    ): readonly [WireValueGetter, ColumnType] {
        const inOutputRow = opts?.inOutputRow ?? false;
        const columnFirst = opts?.columnFirst ?? true;

        const columnValue = getSourceColumnProperty(desc);
        if (columnFirst && columnValue !== undefined) {
            return this.makeColumnGetter(columnValue, inOutputRow, withFormat);
        }

        const stringValue = getStringProperty(desc);
        if (stringValue !== undefined) {
            return [() => stringValue, { kind: "string" }];
        }

        if (columnValue !== undefined) {
            return this.makeColumnGetter(columnValue, inOutputRow, withFormat);
        }

        const switchValue = getSwitchProperty(desc);
        if (switchValue !== undefined) {
            return [() => switchValue, { kind: "boolean" }];
        }

        const enumValue = getEnumProperty(desc);
        if (enumValue !== undefined) {
            // We only support string enums for now.  This is trivial to fix, but
            // it should return the correct column type.
            assert(typeof enumValue === "string");
            return [() => enumValue, { kind: "string" }];
        }

        const specialValueProperty = getSpecialValueProperty(desc);
        if (specialValueProperty !== undefined) {
            switch (specialValueProperty) {
                case SpecialValueKind.Timestamp:
                    return [
                        hb => hb.getGlobalValue(undefined, this.computationModel.getTimestampPath()),
                        { kind: "date-time" },
                    ];
                case SpecialValueKind.VerifiedEmailAddress:
                    return [
                        hb => hb.getGlobalValue(undefined, this.computationModel.getVerifiedEmailAddressPath()),
                        { kind: "email-address" },
                    ];
                case SpecialValueKind.RealEmailAddress:
                    return [
                        hb => hb.getGlobalValue(undefined, this.computationModel.getRealEmailAddressPath()),
                        { kind: "email-address" },
                    ];
                case SpecialValueKind.UniqueIdentifier:
                    return [() => uuid(), { kind: "string" }];
                case SpecialValueKind.ActionSource:
                    return [() => ActionSource.None, { kind: "string" }];
                case SpecialValueKind.CurrentURL:
                    // We're not subscribing to this.  Right now we only use
                    // this special value in actions, so it's fine if it
                    // doesn't update when it changes.
                    return [() => window.location.href, { kind: "uri" }];
                case SpecialValueKind.ClearColumn:
                    return [() => "", { kind: "string" }];
                default:
                    return assertNever(specialValueProperty);
            }
        }

        return [emptyValueGetter, { kind: "string" }];
    }

    public inflatePredicate(predicate: Formula, tableTypeOverride?: TableGlideType): WirePredicate | undefined {
        const condition = decomposePredicateCombinationFormula(predicate)?.spec;
        if (condition === undefined) return undefined;

        const tables: ContextTableTypes =
            definedMap(tableTypeOverride, t => ({
                input: t,
                output: t,
                containingScreen: this.tables.input,
            })) ?? this.tables;
        const preparer: ConditionValuePreparer<WireValueGetter> = {
            makePathForSourceColumn: sc => this.makeColumnGetter(sc, false, false, tables)[0],
            getVerifiedEmailAddressPath: () => hb =>
                hb.getGlobalValue(undefined, this.computationModel.getVerifiedEmailAddressPath()),
            getTimestampPath: () => hb => hb.getGlobalValue(undefined, this.computationModel.getTimestampPath()),
            getStartOrEndOfTodayPath: startOrEnd => hb =>
                hb.getGlobalValue(undefined, this.computationModel.getStartOrEndOfTodayPath(startOrEnd)),
        };
        const conditions = condition.predicates.map(p => prepareCondition(p, preparer));
        return hb => {
            for (const c of conditions) {
                const result = computeCondition(c, getter => getter(hb), parseValueAsDateSync) ?? false;
                if (result && condition.combinator === "or") {
                    return true;
                } else if (!result && condition.combinator === "and") {
                    return false;
                }
            }
            return condition.combinator === "or" ? false : true;
        };
    }

    public inflateFilters(
        filters: readonly FilterArrayTransform[],
        tableTypeOverride: TableGlideType | undefined
    ): WirePredicate {
        const predicates: WirePredicate[] = [];

        for (const filterTransform of filters) {
            const predicate = this.inflatePredicate(filterTransform.predicate, tableTypeOverride);
            if (predicate === undefined) continue;

            predicates.push(predicate);
        }

        return hb => {
            for (const p of predicates) {
                if (!p(hb)) {
                    return false;
                }
            }
            return true;
        };
    }

    public inflateTransforms(tableType: TableGlideType, transforms: readonly ArrayTransform[]): TableTransformer {
        const reverseTransform = findType(
            transforms,
            (t): t is TableOrderArrayTransform => t.kind === ArrayTransformKind.TableOrder && t.reverse
        );
        const shuffleTransform = findType(
            transforms,
            (t): t is ShuffleArrayTransform => t.kind === ArrayTransformKind.Shuffle
        );
        const limitTransform = findType(
            transforms,
            (t): t is LimitArrayTransform => t.kind === ArrayTransformKind.Limit
        );

        const filterTransform = findType(
            transforms,
            (t): t is FilterArrayTransform => t.kind === ArrayTransformKind.Filter
        );
        let filterPredicate: ((hb: WireRowHydrationValueProvider) => boolean) | undefined;
        if (filterTransform !== undefined) {
            filterPredicate = this.inflateFilters([filterTransform], tableType);
        }

        const sortTransform = findType(transforms, (t): t is SortArrayTransform => t.kind === ArrayTransformKind.Sort);
        let sortGetterAndReverse: [WireValueGetter, boolean] | undefined;
        if (sortTransform !== undefined && sortTransform.keys.length > 0) {
            assert(sortTransform.keys.length === 1);
            const { key, order } = sortTransform.keys[0];
            const columnName = decomposeSortKey(key);
            if (columnName !== undefined) {
                const [keyGetter] = this.makeColumnGetter(
                    { kind: SourceColumnKind.DefaultContext, name: [columnName] },
                    false,
                    false,
                    { ...makeInputOutputTables(tableType), containingScreen: undefined }
                );
                sortGetterAndReverse = [keyGetter, order === SortOrder.Descending];
            }
        }

        return (rvpm, table) => {
            let rows = Array.from(table.values()).filter(r => r.$isVisible);

            if (filterPredicate !== undefined) {
                // NOTE: We could be much smarter here.  If there's no sort,
                // then we can just stop gathering rows until we've fulfilled
                // our limit, or however many rows we need to display in the
                // component.
                rows = rows.filter(r => {
                    // TODO: If we have filter and sort we're making value
                    // providers twice by row.
                    const rhb = rvpm.makeRowValueProvider(r);
                    return defined(filterPredicate)(rhb);
                });
            }

            if (sortGetterAndReverse !== undefined) {
                const [keyGetter, reverse] = sortGetterAndReverse;
                const rowsWithKeys = rows.map(r => {
                    const rhb = rvpm.makeRowValueProvider(r);
                    return [keyGetter(rhb), r] as const;
                });

                // ##sorting:
                // These two are copies and must be kept in sync.
                rows = rowsWithKeys
                    .sort(([one], [two]) => {
                        if (typeof one === "string") {
                            one = one.trim().toLowerCase();
                        }
                        if (typeof two === "string") {
                            two = two.trim().toLowerCase();
                        }
                        const result = compareValues(one, two);
                        if (result !== undefined) {
                            return result;
                        }
                        if (one === undefined) {
                            return -1;
                        } else {
                            return 1;
                        }
                    })
                    .map(([, r]) => r);

                if (reverse) {
                    rows = rows.reverse();
                }
            } else if (reverseTransform !== undefined) {
                rows = rows.reverse();
            } else if (shuffleTransform !== undefined) {
                rows = shuffle(rows);
            }
            if (rows === undefined) {
                rows = Array.from(table.values());
            }

            if (limitTransform !== undefined) {
                const numRows = getNumberProperty(limitTransform.numRows);
                if (numRows !== undefined && rows.length > numRows) {
                    rows = rows.slice(0, numRows);
                }
            }

            return rows;
        };
    }

    public getValueGetterForTableComponent(
        desc: LegacyPropertyDescription,
        transforms: readonly ArrayTransform[]
    ): [WireTableGetter, WireInflationBackend] | undefined {
        let getter: (hb: WireRowHydrationValueProvider) => Table | undefined;
        let tableType: TableGlideType | undefined;

        const columnName = getSourceColumnProperty(desc);
        if (columnName !== undefined) {
            const [valueGetter, columnType] = this.makeColumnGetter(columnName, false, false);

            if (!isMultiRelationType(columnType)) return undefined;
            tableType = this.context.findTable(columnType.items);
            if (tableType === undefined) return undefined;

            getter = hb => definedMap(valueGetter(hb), v => asTable(v));
        } else {
            const tableName = getTableProperty(desc);
            if (tableName === undefined) return undefined;

            tableType = this.context.findTable(tableName);

            const maybeGetter = this.makeTableGetter(tableName);
            if (maybeGetter === undefined) return undefined;
            getter = maybeGetter;
        }

        if (getter === undefined || tableType === undefined) return undefined;

        const ib = new InflationBackend(
            this.appFacilities,
            this.context,
            { ...makeInputOutputTables(tableType), containingScreen: this.tables.input },
            this.computationModel,
            this.mutatingScreenKind
        );

        const transformTable = this.inflateTransforms(tableType, transforms);

        const transformedGetter: WireTableGetter = hb => {
            const table = defined(getter)(hb);
            if (table === undefined) return undefined;

            const ttvp = hb.makeTableTransformValueProvider(defined(tableType));
            if (ttvp === undefined) return undefined;

            const rows = transformTable(ttvp, table);

            return ttvp.makeHydrationBackendForTable(rows);
        };

        return [transformedGetter, ib];
    }

    public makeInflationBackendForOutputTable(): InflationBackend {
        return new InflationBackend(
            this.appFacilities,
            this.context,
            { ...makeInputOutputTables(this.tables.output), containingScreen: this.tables.containingScreen },
            this.computationModel,
            this.mutatingScreenKind
        );
    }
}

abstract class HydrationValueProvider<TContext extends ValueProviderContext> implements WireRowHydrationValueProvider {
    protected readonly stateValues: StateValuesMap = new Map();

    constructor(
        protected readonly context: TContext,
        public readonly rowContext: InternalRowContext | undefined,
        protected readonly oldStateValues: StateValuesMap | undefined,
        protected readonly stateSaveKey: string | undefined
    ) {}

    protected abstract addGlobalKey(rootKey: string): void;
    protected abstract addColumnInRow(rootKey: string, rowID: string, columnKey: string): void;

    public getGlobalValue(tableName: TableName | undefined, path: RootPath): GroundValue {
        this.addGlobalKey(path.rest.key);
        if (tableName !== undefined) {
            this.context.fetchTableData(tableName);
        }

        return this.context.namespace?.get(path);
    }

    public getColumnInRow(
        tableName: TableName,
        rootPath: RootPath,
        rowID: string,
        columnPath: RelativePath
    ): GroundValue {
        assert(isTopLevelPath(rootPath));
        assert(isKeyPath(columnPath) && columnPath.rest === undefined);

        this.addColumnInRow(rootPath.rest.key, rowID, columnPath.key);
        this.context.fetchTableData(tableName);

        const row = definedMap(this.context.namespace?.get(rootPath), asTable)?.get(rowID);
        return follow(row, columnPath);
    }

    public getState<T>(
        name: string,
        validate: (v: unknown) => v is T,
        defaultValue: T,
        convert?: (v: unknown) => T
    ): WireEditableValue<T> {
        const newExisting = this.stateValues.get(name);
        if (newExisting !== undefined) {
            const [v, t] = newExisting;
            assert(validate(v));
            return { value: v, onChangeToken: t };
        }

        const token = uuid();
        let value: T;
        // It's important that we only take the value of the old state, and
        // not its token for reuse.  State loading gives us a dummy token, and
        // using that would cause weird effects.
        const existing = this.oldStateValues?.get(name)?.[0];
        if (existing !== undefined && validate(existing)) {
            value = existing;
        } else {
            value = defaultValue;
        }
        this.stateValues.set(name, [value, token, convert ?? (x => x)]);
        return { value, onChangeToken: token };
    }
}

abstract class HydrationBackendBase<TContext extends ValueProviderActionContext>
    extends HydrationValueProvider<TContext>
    implements WireHydrationBackend
{
    // TODO: Maybe this doesn't belong in here and should be abstracted.
    protected readonly tokens = new Set<string>();

    protected addToken(token: string): void {
        this.tokens.add(token);
    }

    public registerAction(run: WireActionRunner): string {
        const token = uuid();
        assert(this.rowContext !== undefined);
        this.context.actions.set(token, [run, this.rowContext]);
        this.addToken(token);
        return token;
    }
}

function addSubscriptionHandler(
    ns: MutableNamespace<unknown>,
    name: string,
    handler: RowSubscriptionHandler
): RootPath {
    const path = ns.addEntity(name, handler, undefined);
    ns.get(path);
    return path;
}

// class GlobalHydrationValueProvider extends HydrationValueProvider<ValueProviderContext> {
//     private readonly globalKeys = new Set<string>();

//     protected addGlobalKey(rootKey: string): void {
//         this.globalKeys.add(rootKey);
//     }

//     protected addColumnInRow(rootKey: string, rowID: string, columnKey: string): void {
//         return panic("We should never access a context ")
//     }

// }

class RowHydrationValueProvider<TContext extends ValueProviderActionContext> extends HydrationBackendBase<TContext> {
    private readonly sources = {
        globalKeys: new Set<string>(),
        columnsInRows: new DefaultMap<string, DefaultMap<string, Set<string>>>(() => new DefaultMap(() => new Set())),
    };
    private subsidiary: RowHydrationValueProvider<TContext> | undefined;

    protected addGlobalKey(rootKey: string): void {
        this.sources.globalKeys.add(rootKey);
    }

    protected addColumnInRow(rootKey: string, rowID: string, columnKey: string): void {
        this.sources.columnsInRows.get(rootKey).get(rowID).add(columnKey);
    }

    private makeSubscriptionHandler(onChange: () => void): RowSubscriptionHandler | undefined {
        if (this.sources.globalKeys.size === 0 && this.sources.columnsInRows.size === 0) return undefined;

        return new RowSubscriptionHandler(this.sources, onChange);
    }

    protected setSubsidiary(subsidiary: RowHydrationValueProvider<TContext>): void {
        assert(this.subsidiary === undefined);
        this.subsidiary = subsidiary;
    }

    // TODO: It's very odd that this class handles state, but then also offers
    // subscription without them.  It seems this needs to be broken down
    // further.
    public subscribe(onChange: () => void): SubscriptionInfo {
        let handlerAndPath: SubscriptionHandlerAndPath | undefined;
        if (this.context.namespace !== undefined) {
            const handler = this.makeSubscriptionHandler(onChange);
            if (handler !== undefined) {
                // TODO: give it a nicer name to help debugging
                const path = addSubscriptionHandler(this.context.namespace, "component", handler);
                handlerAndPath = { handler, path };
            }
        }

        return {
            handlerAndPath,
            tokens: this.tokens,
            subsidiary: this.subsidiary?.subscribe(onChange),
        };
    }

    public subscribeComponent(onChange: () => void): ComponentState {
        return {
            ...this.subscribe(onChange),
            stateValues: this.stateValues,
            stateSaveKey: this.stateSaveKey,
            stateChanged: false,
        };
    }
}

class RowValueProviderMaker implements WireRowValueProviderMaker {
    constructor(
        protected readonly hydrationContext: ScreenHydrationContext,
        private readonly tableName: TableName,
        protected readonly sources: MutableTableSubscriptionSources,
        private readonly containingScreenRow: Row | undefined
    ) {}

    public makeRowValueProvider(row: Row): WireRowHydrationValueProvider {
        return new RowFromTableHydrationValueProvider(
            this.hydrationContext,
            { inputRow: row, outputRow: undefined, containingScreenRow: this.containingScreenRow },
            this.tableName,
            this.sources
        );
    }
}

class RowActionHydrationBackend
    extends RowHydrationValueProvider<ScreenHydrationContext>
    implements WireRowActionHydrationValueProvider
{
    public getIsUserSignedIn(): boolean {
        const email = definedMap(this.context.verifiedEmailAddressPath, p => this.getGlobalValue(undefined, p));
        return email !== undefined;
    }

    public isTabVisible(tabScreenName: string): boolean {
        // FIXME: This will have to subscribe to the visibility condition
        return this.context.visibleTabScreenNames.has(tabScreenName);
    }
}

// We make one of these per component we hydrate, and make it keep track of
// all the values gotten.
class RowComponentHydrationBackend extends RowActionHydrationBackend implements WireRowComponentHydrationBackend {
    private tableBackend: TableComponentHydrationBackend | undefined;

    constructor(
        hydrationContext: ScreenHydrationContext,
        rowScreenContext: InternalRowContext | undefined,
        oldStateValues: StateValuesMap | undefined,
        stateSaveKey: string | undefined,
        private readonly tables: InputOutputTables
    ) {
        super(hydrationContext, rowScreenContext, oldStateValues, stateSaveKey);
    }

    public registerOnValueChange(columnName: string): string | undefined {
        const row = this.rowContext?.outputRow ?? this.rowContext?.inputRow;
        if (row === undefined) return undefined;

        const token = uuid();
        const column = getTableColumn(this.tables.output, columnName);
        if (column === undefined || !isPrimitiveType(column.type)) return;
        this.context.onChangeValueRows.set(token, {
            kind: "column",
            tableName: getTableName(this.tables.output),
            columnName,
            row,
        });
        this.addToken(token);
        return token;
    }

    public makeTableTransformValueProvider(tableType: TableGlideType): WireTableTransformValueProvider {
        const tableName = getTableName(tableType);
        const sources = makeTableSubscriptionSources();
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const rowBackend = this;

        class TableTransformValueProvider extends RowValueProviderMaker implements WireTableTransformValueProvider {
            // This must be called (and the row backend must be subscribed),
            // or everything depended on in `makeRowValueProvider` will not
            // actually be listened to.
            public makeHydrationBackendForTable(rows: Row[]): WireTableComponentHydrationBackend {
                assert(rowBackend.tableBackend === undefined);

                rowBackend.tableBackend = new TableComponentHydrationBackend(
                    this.hydrationContext,
                    rows,
                    makeInputOutputTables(tableType),
                    this.sources
                );
                return rowBackend.tableBackend;
            }
        }

        return new TableTransformValueProvider(this.context, tableName, sources, rowBackend.rowContext?.inputRow);
    }

    public subscribeComponent(onChange: () => void): ComponentState {
        let info = super.subscribeComponent(onChange);
        // TODO: This class shouldn't handle subsidiaries, but just call
        // `setSubsidiary` from `RowHydrationValueProvider`.  In fact, we will
        // probably have to handle multiple subsidiaries at some point.
        if (this.tableBackend !== undefined) {
            assert(info.subsidiary === undefined);
            info = { ...info, subsidiary: this.tableBackend?.subscribe(onChange) };
        }
        return info;
    }

    public makeHydrationBackendForOutputRow(): WireRowComponentHydrationBackend | undefined {
        if (this.rowContext?.outputRow === undefined) return undefined;

        const rowContext: InternalRowContext = {
            inputRow: this.rowContext.outputRow,
            outputRow: undefined,
            containingScreenRow: this.rowContext.containingScreenRow,
        };
        const hb = new RowComponentHydrationBackend(
            this.context,
            rowContext,
            this.oldStateValues,
            this.stateSaveKey,
            this.tables
        );
        this.setSubsidiary(hb);
        return hb;
    }
}

interface MutableTableSubscriptionSources {
    readonly globalKeys: Set<string>;
    // root key -> column names
    readonly columnsInDirectRows: DefaultMap<string, Set<string>>;
    // If one of these gets dirtied, it dirties all rows.
    readonly columnsInIndirectRows: DefaultMap<string, Set<string>>;
}

function makeTableSubscriptionSources(): MutableTableSubscriptionSources {
    return {
        globalKeys: new Set<string>(),
        columnsInDirectRows: new DefaultMap<string, Set<string>>(() => new Set()),
        columnsInIndirectRows: new DefaultMap<string, Set<string>>(() => new Set()),
    };
}

// This will modify the data in `sources` during hydration.
class RowFromTableHydrationValueProvider implements WireRowHydrationValueProvider {
    constructor(
        protected readonly context: ScreenHydrationContext,
        public readonly rowContext: InternalRowContext,
        private readonly tableName: TableName,
        private readonly sources: MutableTableSubscriptionSources
    ) {}

    public getGlobalValue(tableName: TableName | undefined, path: RootPath): GroundValue {
        this.sources.globalKeys.add(path.rest.key);
        if (tableName !== undefined) {
            this.context.fetchTableData(tableName);
        }

        return this.context.namespace?.get(path);
    }

    public getColumnInRow(
        tableName: TableName,
        rootPath: RootPath,
        rowID: string,
        columnPath: RelativePath
    ): GroundValue {
        assert(isTopLevelPath(rootPath));
        assert(isKeyPath(columnPath) && columnPath.rest === undefined);

        const isDirect = rowID === this.rowContext.inputRow?.$rowID && areTableNamesEqual(tableName, this.tableName);
        (isDirect ? this.sources.columnsInDirectRows : this.sources.columnsInIndirectRows)
            .get(rootPath.rest.key)
            .add(columnPath.key);
        this.context.fetchTableData(tableName);

        const r = definedMap(this.context.namespace?.get(rootPath), asTable)?.get(rowID);
        return follow(r, columnPath);
    }
}

class RowFromTableHydrationBackend
    extends RowFromTableHydrationValueProvider
    implements WireRowComponentHydrationBackend
{
    constructor(
        context: ScreenHydrationContext,
        rowScreenContext: InternalRowContext,
        private readonly tables: InputOutputTables,
        sources: MutableTableSubscriptionSources,
        private readonly tokens: Set<string>,
        private readonly registerActionWithContext: (
            run: WireActionRunner,
            rowScreenContext: InternalRowContext | undefined
        ) => string
    ) {
        super(context, rowScreenContext, getTableName(tables.input), sources);
    }

    public registerAction(run: WireActionRunner): string {
        return this.registerActionWithContext(run, this.rowContext);
    }

    public registerOnValueChange(columnName: string): string | undefined {
        const row = this.rowContext.inputRow;
        if (row === undefined) return undefined;

        // TODO: This is duplicated in `RowComponentHydrationBackend`
        const column = getTableColumn(this.tables.output, columnName);
        if (column === undefined || !isPrimitiveType(column.type)) return undefined;

        const token = uuid();
        this.context.onChangeValueRows.set(token, {
            kind: "column",
            tableName: getTableName(this.tables.output),
            columnName,
            row,
        });
        this.tokens.add(token);
        return token;
    }

    public makeTableTransformValueProvider() {
        return panic("Cannot make a table from a row in a table");
    }

    public getState<T>(
        _name: string,
        _validate: (v: unknown) => v is T,
        _defaultValue: T,
        _convert: (v: unknown) => T
    ): WireEditableValue<T> {
        return panic("State is not supported in list items yet");
    }

    // TODO: These two are repeated above.  It would be nice to just have them
    // once, especially if we add more.
    public getIsUserSignedIn(): boolean {
        const email = definedMap(this.context.verifiedEmailAddressPath, p => this.getGlobalValue(undefined, p));
        return email !== undefined;
    }

    public isTabVisible(tabScreenName: string): boolean {
        return this.context.visibleTabScreenNames.has(tabScreenName);
    }

    public makeHydrationBackendForOutputRow(): WireRowComponentHydrationBackend | undefined {
        return panic("We don't support this yet");
    }
}

class TableComponentHydrationBackend implements WireTableComponentHydrationBackend {
    private readonly tokens = new Set<string>();

    constructor(
        private readonly context: ScreenHydrationContext,
        private rows: readonly Row[],
        private readonly tables: InputOutputTables,
        private readonly sources: MutableTableSubscriptionSources = makeTableSubscriptionSources()
    ) {
        assert(tables.input === tables.output);
    }

    public get tableScreenContext(): readonly Row[] {
        return this.rows;
    }

    public registerAction(run: WireActionRunner, rowContext?: InternalRowContext): string {
        const token = uuid();
        this.context.actions.set(token, [run, rowContext ?? emptyInternalScreenContext]);
        this.tokens.add(token);
        return token;
    }

    public makeHydrationBackendForRow(row: Row): WireRowComponentHydrationBackend {
        const rowScreenContext: InternalRowContext = {
            inputRow: row,
            outputRow: undefined,
            containingScreenRow: undefined,
        };

        return new RowFromTableHydrationBackend(
            this.context,
            rowScreenContext,
            this.tables,
            this.sources,
            this.tokens,
            (run, ctx) => this.registerAction(run, ctx)
        );
    }

    private makeSubscriptionHandler(onChange: () => void): TableSubscriptionHandler | undefined {
        if (
            this.sources.globalKeys.size === 0 &&
            this.sources.columnsInDirectRows.size === 0 &&
            this.sources.columnsInIndirectRows.size === 0
        ) {
            return undefined;
        }

        return new TableSubscriptionHandler(this.sources, onChange);
    }

    public subscribe(onChange: () => void): ComponentState {
        let handlerAndPath: SubscriptionHandlerAndPath | undefined;
        if (this.context.namespace !== undefined) {
            const handler = this.makeSubscriptionHandler(onChange);
            if (handler !== undefined) {
                // TODO: Include the screen name in the title for debugging
                const path = addSubscriptionHandler(this.context.namespace, "arrayScreen", handler);
                handlerAndPath = { handler, path };
            }
        }

        return {
            handlerAndPath,
            tokens: this.tokens,
            subsidiary: undefined,
            stateValues: new Map(),
            stateSaveKey: "NOT SUPPORTED",
            stateChanged: false,
        };
    }

    // We could alternatively make a new instance with the new rows, but we'd have to be careful
    // to keep modifying the same state as this instance, or subscribing won't work.
    public withRows(rows: readonly Row[]): WireTableComponentHydrationBackend {
        this.rows = rows;
        return this;
    }
}

type ScreenContextGetter = (
    hydrationContext: ScreenHydrationContext,
    oldSubscriptionInfo: SubscriptionInfo | undefined
) => [InternalScreenContext, SubscriptionInfo | undefined];

interface InflatedClassScreen {
    readonly tables: InputOutputTables;
    readonly componentHydrators: WireComponentHydratorWithID[];
    readonly titleGetter: WireValueGetter;

    readonly getScreenContext: ScreenContextGetter;
}

interface InflatedArrayScreen {
    readonly table: TableGlideType;
    readonly contentHydrator: WireTableComponentHydrator;

    getContextTable(
        hydrationContext: ScreenHydrationContext,
        oldSubscriptionInfo: SubscriptionInfo | undefined
    ): [Table, SubscriptionInfo] | undefined;
}

interface InflatedUserProfile {
    readonly emailValueGetter: WireValueGetter;
    readonly nameValueGetter: WireValueGetter | undefined;
}

interface ValueProviderContext {
    readonly namespace: MutableNamespace<unknown> | undefined;

    fetchTableData(tableName: TableName): void;
}

interface ValueProviderActionContext extends ValueProviderContext {
    readonly actions: ActionTokenMap;
}

interface ScreenHydrationContext extends ValueProviderActionContext {
    readonly internalScreen: InternalScreen;
    readonly onChangeValueRows: OnChangeDataTokenMap;
    readonly position: PageScreenPosition;
    readonly depth: number;
    readonly visibleTabScreenNames: ReadonlySet<string>;
    readonly verifiedEmailAddressPath: RootPath | undefined;
}

interface ScreenHydrationResult {
    readonly hydratedScreen: HydratedScreen;
    readonly context: InternalScreenContext;
}

enum State {
    Clean,
    Requested,
    Recomputing,
    FollowUpRequested,
    Retired,
}

interface ParsedScreen {
    readonly screenName: string;
    readonly rowID: string | undefined;
    readonly title: string | undefined;
    readonly depth: number;
}

interface ParsedPath {
    readonly tabScreenName: string;
    readonly screen: ParsedScreen;
    readonly sideScreen: ParsedScreen | undefined;
    readonly modal: ParsedScreen | undefined;
}

// Screen prefixes:
//
// s ... primary screen
// w ... side screen
// m ... modal
//
// After each screen:
//
// r ... row ID
// t ... title
// d ... depth

type ScreenPrefix = "s" | "w" | "m";

function normalizeSlug(slug: string): string {
    return slug.trim();
}

function getRowIndexForRow(table: TableGlideType, row: Row): RowIndex | undefined {
    const maybeRowIndex = getRowColumn(row, rowIndexColumnName);
    let rowIndexHint: BaseRowIndex | undefined;
    if (isBaseRowIndex(maybeRowIndex)) {
        rowIndexHint = maybeRowIndex;
    }

    if (table.rowIDColumn !== undefined) {
        const rowID = getRowColumn(row, table.rowIDColumn);
        if (typeof rowID !== "string") return;

        return { keyColumnName: table.rowIDColumn, keyColumnValue: rowID, rowIndexHint };
    } else if (rowIndexHint !== undefined) {
        return rowIndexHint;
    } else {
        return undefined;
    }
}

const dummyUserProfileTable: TableGlideType = {
    name: "$userProfiles",
    columns: [],
};
const dummyUserProfileTables: ContextTableTypes = {
    ...makeInputOutputTables(dummyUserProfileTable),
    containingScreen: undefined,
};

interface WireBackendCallbacks extends WireActionCallbacks {
    // These are callbacks that could go to the frontend, but they're
    // synchronous, and not easily made asynchronous.  It's not clear we'll
    // actually ever need them to be async, because we might not need to
    // implement them in the frontend, so we're leaving this for now.
    loadStateValues(key: string): Record<string, unknown>;
    saveStateValues(key: string, values: Record<string, unknown>): void;
}

type ActionBackendWithHydrationContext = WireActionBackend & { readonly hydrationContext: ValueProviderActionContext };

function inflateFilters(ib: InflationBackend | undefined, filters: readonly FilterArrayTransform[]): WirePredicate {
    if (ib !== undefined) {
        return ib.inflateFilters(filters, undefined);
    } else {
        for (const filterTransform of filters) {
            if (filterTransform.isActive === false) continue;
            const condition = decomposePredicateCombinationFormula(filterTransform.predicate)?.spec;
            if (condition === undefined || condition.predicates.length > 0 || condition.combinator !== "and") {
                return () => false;
            }
        }
        return () => true;
    }
}

function normalizeURLPath(urlPath: string): string {
    if (urlPath.startsWith("/")) {
        urlPath = urlPath.substring(1);
    }
    return urlPath;
}

export class WireBackendImpl implements WireBackend {
    private readonly navigationModelWatchable = new Watchable<WireNavigationModel>({
        urlPath: "/",
        tabs: [],
        flyoutItems: [],
        screen: emptyScreen,
        removeBranding: true,
    });
    private readonly metadataWatchable = new Watchable<NavigationModelData | undefined>(undefined);
    private parsedPath: ParsedPath | undefined;
    // This has to be brought in sync with `parsedPath` when recomputing.
    private internalNavigationModel: InternalNavigationModel | undefined;
    private readonly inflatedClassScreens = new Map<string, InflatedClassScreen>();
    private readonly inflatedArrayScreens = new Map<string, InflatedArrayScreen>();
    private inflatedUserProfile: InflatedUserProfile | undefined;

    private readonly globalActions: ActionTokenMap = new Map();
    private readonly navigateToTabActions = new DefaultMap<string, WireAction>(tabScreenName => {
        const token = uuid();
        this.globalActions.set(token, [
            async ab => {
                ab.navigateToTab(tabScreenName);
                return true;
            },
            emptyInternalScreenContext,
        ]);
        return { token };
    });
    private readonly showUserProfileAction: WireAction;
    private readonly signInAction: WireAction;

    private state = State.Clean;
    // TODO: This is only for debugging - remove
    private readonly id = uuid();

    constructor(
        private readonly appID: string,
        private readonly appFacilities: AppFacilities,
        private readonly dataStore: DataStore,
        private readonly context: ExistingAppDescriptionContext,
        private readonly callbacks: WireBackendCallbacks,
        private readonly isBuilder: boolean,
        urlPathOrPrevious: string | WireBackendImpl | undefined
    ) {
        logInfo("constructing", this.id);

        let parsedPath: ParsedPath | undefined;
        if (typeof urlPathOrPrevious === "string") {
            parsedPath = this.parseURLPath(urlPathOrPrevious);
        } else if (urlPathOrPrevious !== undefined) {
            parsedPath = urlPathOrPrevious.parsedPath;
        }
        if (parsedPath === undefined || !this.isParsedPathValid(parsedPath)) {
            parsedPath = this.makeRootParsedPath();
        }

        this.parsedPath = parsedPath;

        this.showUserProfileAction = { token: uuid() };
        this.globalActions.set(this.showUserProfileAction.token, [
            async ab => {
                ab.pushUserProfileScreen(undefined, PageScreenPosition.Modal);
                return true;
            },
            emptyInternalScreenContext,
        ]);

        this.signInAction = { token: uuid() };
        this.globalActions.set(this.signInAction.token, [
            async ab => {
                ab.actionCallbacks.signIn();
                return true;
            },
            emptyInternalScreenContext,
        ]);

        dataStore.computationModelObservable.subscribe(this.onComputationModelChange);

        this.requestSyncRecompute();
    }

    private get appDescription(): AppDescription {
        return this.context.appDescription;
    }

    public get wireNavigationModelObservable(): ChangeObservable<WireNavigationModel> {
        return this.navigationModelWatchable;
    }

    public get metadataObservable(): ChangeObservable<NavigationModelData | undefined> {
        return this.metadataWatchable;
    }

    private get computationModel(): ComputationModel | undefined {
        return this.dataStore.computationModelObservable.current;
    }

    private findTab(tabScreenName: string): [TabDescription, number] | undefined {
        const tabs = getAppTabs(this.appDescription);
        const index = tabs.findIndex(t => getScreenProperty(t.screenName) === tabScreenName);
        if (index < 0) return undefined;
        return [tabs[index], index];
    }

    private makeMetadata(): NavigationModelData | undefined {
        let internalScreen: InternalScreen | undefined;
        let screenName: string | undefined;
        if (this.parsedPath?.modal !== undefined) {
            screenName = this.parsedPath.modal.screenName;
            internalScreen = this.internalNavigationModel?.modal;
        } else if (this.parsedPath?.sideScreen !== undefined) {
            screenName = this.parsedPath.sideScreen.screenName;
            internalScreen = this.internalNavigationModel?.sideScreen;
        } else if (this.parsedPath?.screen !== undefined) {
            screenName = this.parsedPath.screen.screenName;
            internalScreen = this.internalNavigationModel?.screen;
        } else {
            return undefined;
        }
        const { tabScreenName } = this.parsedPath;

        const [, currentLogicalTabIndex] = defined(this.findTab(tabScreenName));

        const screen: ScreenDescription | undefined = this.appDescription.screenDescriptions[screenName];
        const mutatingScreenKind = getMutatingKindForScreen(screenName, screen);
        const navigationModel: BuilderNavigationModel = {
            currentLogicalTabIndex,
            currentScreenHasFlag: f => {
                if (f === ScreenFlag.IsUserProfileScreen) {
                    return screenName === userProfileScreenName;
                }
                if (screen === undefined) return f === ScreenFlag.IsErrorScreen;
                switch (f) {
                    case ScreenFlag.IsEditScreen:
                        return mutatingScreenKind === MutatingScreenKind.EditScreen;
                    case ScreenFlag.IsAddItemScreen:
                        return mutatingScreenKind === MutatingScreenKind.AddScreen;
                    case ScreenFlag.IsFormScreen:
                        return mutatingScreenKind === MutatingScreenKind.FormScreen;
                    case ScreenFlag.IsTopScreen:
                        return screenName === tabScreenName;
                    case ScreenFlag.IsArrayScreen:
                        return screen.kind === ScreenDescriptionKind.Array;
                    case ScreenFlag.IsMapScreen:
                    case ScreenFlag.IsUnconfigurable:
                    case ScreenFlag.IsSignaturePad:
                    case ScreenFlag.IsShareScreen:
                    case ScreenFlag.IsFinalTransient:
                    case ScreenFlag.IsErrorScreen:
                        return false;
                    default:
                        return assertNever(f);
                }
            },
        };
        let tables: InputOutputTables | undefined;
        if (isClassOrArrayScreenDescription(screen)) {
            tables = makeInputOutputTablesForClassOrArrayScreen(screen, t => this.context.findTable(t));
        }
        return { screenName, screen, mutatingScreenKind, navigationModel, context: internalScreen?.context, tables };
    }

    private unparseParsedPath(parsed: ParsedPath): string {
        const components: string[] = [];

        function pushScreenName(screenName: string) {
            components.push(md5(screenName).substring(0, 6));
        }

        const tab = this.findTab(parsed.tabScreenName)?.[0];
        if (tab?.slug !== undefined) {
            components.push(encodeURIComponent(normalizeSlug(tab.slug)));
        } else {
            pushScreenName(parsed.tabScreenName);
        }

        function unparseScreen(screen: ParsedScreen, prefix: ScreenPrefix) {
            components.push(prefix);
            pushScreenName(screen.screenName);
            if (screen.rowID !== undefined) {
                components.push("r", screen.rowID);
            }
            if (!isEmptyOrUndefined(screen.title)) {
                components.push("t", screen.title);
            }
            if (screen.depth !== 0) {
                components.push("d", screen.depth.toString());
            }
        }

        unparseScreen(parsed.screen, "s");
        if (parsed.sideScreen !== undefined) {
            unparseScreen(parsed.sideScreen, "w");
        }
        if (parsed.modal !== undefined) {
            unparseScreen(parsed.modal, "m");
        }

        return components.map(encodeURIComponent).join("/");
    }

    private parseURLPath(urlPath: string): ParsedPath | undefined {
        const screenNames = [...Object.keys(this.appDescription.screenDescriptions), userProfileScreenName].map(
            sn => [sn, md5(sn)] as const
        );

        urlPath = normalizeURLPath(urlPath);
        const components = urlPath.split("/").map(decodeURIComponent);

        const parseScreenName = () => {
            const hash = components[0];
            if (hash === undefined || hash.length <= 0) return undefined;

            const entry = screenNames.find(([, h]) => h.startsWith(hash));
            if (entry === undefined) return undefined;

            components.shift();

            return entry[0];
        };

        const parseScreen = (prefix: ScreenPrefix): ParsedScreen | undefined => {
            // If I substitute `first()` with its body below, TS thinks that
            // the first check means that `components[0] === "s"` forever
            // after, despite the `unshift`s in between.
            function first() {
                return components[0];
            }

            if (first() !== prefix) return undefined;
            components.shift();

            const screenName = parseScreenName();
            if (screenName === undefined) return undefined;

            let rowID: string | undefined;
            if (first() === "r" && components[1] !== undefined && components[1].length > 0) {
                rowID = components[1];
                components.shift();
                components.shift();
            }

            let title: string | undefined;
            if (first() === "t" && components[1] !== undefined) {
                title = components[1];
                components.shift();
                components.shift();
            }

            let depth: number | undefined;
            if (first() === "d" && components[1] !== undefined) {
                depth = parseInt(components[1], 10);
                if (isNaN(depth) || depth < 0) {
                    depth = undefined;
                }
            }

            return { screenName, rowID, title, depth: depth ?? 0 };
        };

        let tabScreenName: string | undefined;
        for (const tab of getAppTabs(this.appDescription)) {
            const { slug } = tab;
            if (slug !== undefined && components[0] === normalizeSlug(slug)) {
                tabScreenName = getScreenProperty(tab.screenName);
                if (tabScreenName !== undefined) {
                    components.shift();
                    break;
                }
            }
        }
        if (tabScreenName === undefined) {
            tabScreenName = parseScreenName();
            if (tabScreenName === undefined) return undefined;
        }

        let screen = parseScreen("s");
        let sideScreen: ParsedScreen | undefined;
        let modal: ParsedScreen | undefined;
        if (screen === undefined) {
            screen = { screenName: tabScreenName, rowID: undefined, title: undefined, depth: 0 };
        } else {
            sideScreen = parseScreen("w");
            modal = parseScreen("m");
        }

        return { tabScreenName, screen, sideScreen, modal };
    }

    private isParsedPathValid(path: ParsedPath): boolean {
        if (this.appDescription.screenDescriptions[path.tabScreenName] === undefined) return false;
        const foundTab = this.findTab(path.tabScreenName)?.[0];
        if (foundTab === undefined || foundTab.hidden) return false;

        const checkScreen = (parsed: ParsedScreen) => {
            const screen = this.appDescription.screenDescriptions[parsed.screenName];
            if (screen === undefined) {
                return parsed.screenName === userProfileScreenName;
            }

            if (screen.kind !== ScreenDescriptionKind.Class) return false;
            if (screen.fetchesData !== true && parsed.rowID === undefined) return false;

            return true;
        };

        if (!checkScreen(path.screen)) return false;
        if (path.sideScreen !== undefined && !checkScreen(path.sideScreen)) return false;
        if (path.modal !== undefined && !checkScreen(path.modal)) return false;

        return true;
    }

    private makeRootParsedPath(): ParsedPath | undefined {
        const tab = getAppTabs(this.appDescription).find(t => !t.hidden);
        const screenName = getScreenProperty(tab?.screenName);
        if (screenName === undefined) return undefined;

        return {
            tabScreenName: screenName,
            screen: { screenName, rowID: undefined, title: tab?.title, depth: 0 },
            sideScreen: undefined,
            modal: undefined,
        };
    }

    private getRowIndexRootPath(tableName: TableName): RootPath {
        const [[, rowIndexRootPath]] = defined(
            defined(this.computationModel).getColumnPaths(tableName)?.get(rowIndexColumnName)
        );
        return defined(rowIndexRootPath);
    }

    private retireSubscriptionInfo(si: SubscriptionInfo): void {
        if (si.handlerAndPath !== undefined) {
            this.computationModel?.ns.deleteEntity(si.handlerAndPath.path);
        }
        if (si.subsidiary !== undefined) {
            this.retireSubscriptionInfo(si.subsidiary);
        }
    }

    private retireHydratedScreen(hs: HydratedScreen): void {
        if (hs.screenContextSubscriptionInfo !== undefined) {
            this.retireSubscriptionInfo(hs.screenContextSubscriptionInfo);
        }
        for (const si of hs.componentStates) {
            this.retireSubscriptionInfo(si);
        }
        if (hs.titleSubscriptionInfo !== undefined) {
            this.retireSubscriptionInfo(hs.titleSubscriptionInfo);
        }
    }

    private retireScreen(screen: InternalScreen): void {
        const {
            context: { outputRow },
            hydratedScreen,
        } = screen;
        if (hydratedScreen !== undefined) {
            this.retireHydratedScreen(hydratedScreen);
        }
        if (outputRow !== undefined) {
            assert(!outputRow.$isVisible);

            const { computationModel } = this;
            if (computationModel === undefined) return;

            // FIXME: get table keeper and delete the invisible row
        }
    }

    public retire(): void {
        logInfo("retiring", this.id);

        assert(
            this.state !== State.Recomputing && this.state !== State.FollowUpRequested && this.state !== State.Retired
        );
        this.state = State.Retired;

        this.dataStore.computationModelObservable.unsubscribe(this.onComputationModelChange);

        if (this.internalNavigationModel !== undefined) {
            this.retireScreen(this.internalNavigationModel.screen);
            if (this.internalNavigationModel.sideScreen !== undefined) {
                this.retireScreen(this.internalNavigationModel.sideScreen);
            }
            if (this.internalNavigationModel.modal !== undefined) {
                this.retireScreen(this.internalNavigationModel.modal);
            }
        }
    }

    private makeInflationBackend(
        tables: ContextTableTypes,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): InflationBackend | undefined {
        const { computationModel } = this;
        if (computationModel === undefined) return undefined;

        return new InflationBackend(
            this.appFacilities,
            this.context,
            { ...tables, containingScreen: undefined },
            computationModel,
            mutatingScreenKind
        );
    }

    private inflateClassScreenCommon(
        screen: ClassScreenDescription,
        mutatingScreenKind: MutatingScreenKind | undefined
    ):
        | {
              tables: InputOutputTables;
              componentHydrators: WireComponentHydratorWithID[];
              titleGetter: WireValueGetter;
              transformTable: TableTransformer;
              ib: InflationBackend;
          }
        | undefined {
        const tables = makeInputOutputTablesForClassOrArrayScreen(screen, t => this.context.findTable(t));
        if (tables === undefined) return undefined;

        this.dataStore.fetchTableRows(getTableName(tables.input), false);

        const ib = this.makeInflationBackend({ ...tables, containingScreen: undefined }, mutatingScreenKind);
        if (ib === undefined) return undefined;

        const componentHydrators = mapFilterUndefined(getScreenComponents(screen), desc => inflateComponent(ib, desc));

        const [titleGetter] = ib.getValueGetterForProperty(screen.title, true);

        const transformTable = ib.inflateTransforms(tables.input, screen.transforms ?? []);

        return { tables, componentHydrators, titleGetter, transformTable, ib };
    }

    private inflateClassScreen(screenName: string, screen: ClassScreenDescription): InflatedClassScreen | undefined {
        const mutatingScreenKind = getMutatingKindForScreen(screenName, screen);
        const common = this.inflateClassScreenCommon(screen, mutatingScreenKind);
        const { computationModel } = this;
        if (common === undefined || computationModel === undefined) return undefined;
        const { tables, componentHydrators, titleGetter, transformTable, ib } = common;

        if (screen.canEdit === true) {
            // FIXME: This should be a top bar menu item, or we don't support it
            componentHydrators.unshift([
                hb => {
                    const inputRow = hb.rowContext?.inputRow;
                    if (inputRow === undefined) return undefined;

                    const action = registerActionRunner(hb, async ab => {
                        ab.pushDefaultEditScreen(
                            getTableName(tables.input),
                            undefined,
                            inputRow,
                            PageScreenPosition.Modal
                        );
                        return true;
                    });

                    return {
                        kind: WireComponentKind.Button,
                        title: "Edit",
                        icon: undefined,
                        appearance: Appearance.Bordered,
                        action,
                    };
                },
                undefined,
            ]);
        }

        const outputTableName = getTableName(tables.output);

        if (
            mutatingScreenKind === MutatingScreenKind.EditScreen ||
            mutatingScreenKind === MutatingScreenKind.FormScreen
        ) {
            assert(isAddOrEditOrFormScreenDescription(screenName, screen));
            const outputValueGetters = inflateColumnAssignments(
                ib,
                outputTableName,
                getColumnAssignments(screen),
                true,
                undefined
            );
            const onSubmitHydrator = inflateActions(
                ib.makeInflationBackendForOutputTable(),
                getEditOnSubmitActions(screen)
            );

            componentHydrators.push([
                hb => {
                    const { rowContext: rowScreenContext } = hb;
                    assert(rowScreenContext !== undefined);
                    const { outputRow } = rowScreenContext;
                    assert(outputRow !== undefined);

                    let onSubmit: WireActionRunner | undefined;
                    if (onSubmitHydrator !== undefined) {
                        const ohb = hb.makeHydrationBackendForOutputRow();
                        if (ohb !== undefined) {
                            onSubmit = onSubmitHydrator(ohb);
                        }
                    }

                    const columnAssignmentValues = fromPairs(outputValueGetters?.map(([n, g]) => [n, g(hb)]));

                    let saveChanges: WireActionRunner;
                    if (mutatingScreenKind === MutatingScreenKind.EditScreen) {
                        saveChanges = ab => ab.saveEditScreenChanges(outputTableName, rowScreenContext);
                    } else {
                        saveChanges = ab => ab.addRow(outputTableName, outputRow);
                    }

                    const action = registerActionRunner(hb, async ab => {
                        this.setColumnsInInvisibleRow(outputTableName, outputRow, columnAssignmentValues);
                        const success = await saveChanges(ab);
                        if (!success) return false;
                        ab.closeScreen(PageScreenPosition.Modal);
                        return (await onSubmit?.(ab)) ?? true;
                    });

                    return {
                        kind: WireComponentKind.Button,
                        title: "Done",
                        icon: undefined,
                        appearance: Appearance.Bordered,
                        action,
                    };
                },
                undefined,
            ]);
        }

        return {
            tables,
            componentHydrators,
            titleGetter,
            getScreenContext: (hydrationContext, oldSubscriptionInfo) => {
                if (screen.fetchesData === true) {
                    const tableName = getTableName(tables.input);

                    // FIXME: Don't get new context if subscription is not dirty.
                    // FIXME: Don't make new subscription if we have one.

                    // TODO: I'm not sure this subscription stuff should be
                    // in here, or cloistered off somewhere else.
                    if (oldSubscriptionInfo !== undefined) {
                        this.retireSubscriptionInfo(oldSubscriptionInfo);
                    }

                    hydrationContext.fetchTableData(tableName);
                    const tableData = computationModel.getBaseDataForTable(tableName);

                    const sources = makeTableSubscriptionSources();
                    const rvpm = new RowValueProviderMaker(hydrationContext, tableName, sources, undefined);

                    const rows = definedMap(tableData, t => transformTable(rvpm, t)) ?? [];

                    const rowIndexRootPath = this.getRowIndexRootPath(tableName);
                    // TODO: We don't need to subscribe to the row index if
                    // the transformer subscribes to any column.
                    sources.columnsInDirectRows.get(rowIndexRootPath.rest.key).add(rowIndexColumnName);
                    const handler = new TableSubscriptionHandler(sources, this.onDataChange);
                    const subscriptionPath = addSubscriptionHandler(computationModel.ns, "classScreenContext", handler);
                    const subscriptionInfo: SubscriptionInfo = {
                        handlerAndPath: {
                            handler,
                            path: subscriptionPath,
                        },
                        tokens: new Set(),
                        subsidiary: undefined,
                    };

                    let screenContext: InternalScreenContext;
                    if (rows.length === 0) {
                        screenContext = emptyInternalScreenContext;
                    } else {
                        screenContext = {
                            inputRow: rows[0],
                            outputRow: undefined,
                        };
                    }

                    return [screenContext, subscriptionInfo];
                } else {
                    return [hydrationContext.internalScreen.context, oldSubscriptionInfo];
                }
            },
        };
    }

    private inflateUserProfileScreen(screen: ClassScreenDescription | undefined): InflatedClassScreen | undefined {
        let tables: InputOutputTables;
        let componentHydrators: WireComponentHydratorWithID[];
        let getScreenContext: ScreenContextGetter;

        if (screen !== undefined) {
            // We ignore the table transformer here because the user profile
            // screen isn't supposed to have transforms.
            const common = this.inflateClassScreenCommon(screen, undefined);
            if (common === undefined) return undefined;

            tables = common.tables;
            componentHydrators = common.componentHydrators;

            getScreenContext = (_hydrationContext, oldSubscriptionInfo) => {
                if (oldSubscriptionInfo !== undefined) {
                    this.retireSubscriptionInfo(oldSubscriptionInfo);
                }

                let row: Row | undefined;
                let subscriptionInfo: SubscriptionInfo | undefined;
                if (this.computationModel !== undefined) {
                    const path = this.computationModel.getUserProfileRowPath();
                    if (path !== undefined) {
                        const handler = new RowSubscriptionHandler(
                            {
                                globalKeys: new Set([path.rest.key]),
                                columnsInRows: new Map(),
                            },
                            this.onDataChange
                        );
                        const subscriptionPath = addSubscriptionHandler(
                            this.computationModel.ns,
                            "userProfileScreenContext",
                            handler
                        );
                        subscriptionInfo = {
                            handlerAndPath: { handler, path: subscriptionPath },
                            tokens: new Set(),
                            subsidiary: undefined,
                        };
                        row = definedMap(this.computationModel.ns.get(path), asRow);
                    }
                }
                return [{ inputRow: row, outputRow: undefined, containingScreenRow: undefined }, subscriptionInfo];
            };
        } else {
            const userProfileDesc: UserProfileComponentDescription = {
                kind: ComponentKindUserProfile,
                // FIXME: This should be the fallback username
                titleProperty: undefined,
                subtitleProperty: makeSpecialValueProperty(SpecialValueKind.RealEmailAddress),
                imageURLProperty: undefined,
                componentID: makeRowID(),
                visibilityFilters: undefined,
            };

            tables = dummyUserProfileTables;

            const ib = this.makeInflationBackend({ ...tables, containingScreen: undefined }, undefined);
            if (ib === undefined) return undefined;

            const hydrator = inflateComponent(ib, userProfileDesc);
            componentHydrators = definedMap(hydrator, h => [h]) ?? [];

            getScreenContext = () => [emptyInternalScreenContext, undefined];
        }

        return {
            tables,
            getScreenContext,
            titleGetter: () => getLocalizedString("profile"),
            componentHydrators,
        };
    }

    private loadStateValues(key: string | undefined): StateValuesMap | undefined {
        if (key === undefined) return undefined;
        const values = this.callbacks.loadStateValues(key);

        return new Map(Object.entries(values).map(([k, v]) => [k, [v, "DUMMY", x => x]]));
    }

    private saveStateValues(key: string | undefined, values: StateValuesMap): void {
        if (key === undefined) return;

        this.callbacks.saveStateValues(key, fromPairs(Array.from(values.entries()).map(([k, [v]]) => [k, v])));
    }

    private hydrateClassScreen(
        screen: ClassScreenDescription | undefined,
        hydrationContext: ScreenHydrationContext
    ): ScreenHydrationResult | undefined {
        const { internalScreen } = hydrationContext;
        const { hydratedScreen, screenName, defaultTitle } = internalScreen;
        let inflatedScreen = this.inflatedClassScreens.get(screenName);
        if (inflatedScreen === undefined) {
            if (screenName === userProfileScreenName) {
                inflatedScreen = this.inflateUserProfileScreen(screen);
            } else {
                inflatedScreen = this.inflateClassScreen(screenName, defined(screen));
            }
            if (inflatedScreen === undefined) return undefined;
            this.inflatedClassScreens.set(screenName, inflatedScreen);
        }

        const { tables, componentHydrators, titleGetter, getScreenContext } = inflatedScreen;
        const [screenContext, screenContextSubscriptionInfo] = getScreenContext(
            hydrationContext,
            hydratedScreen?.screenContextSubscriptionInfo
        );

        const makeHydrationBackend = (cs: ComponentState | undefined, componentID: string | undefined) => {
            const stateSaveKey = makeStateSaveKey(hydrationContext, componentID);
            const stateValues = definedMap(cs, s => s.stateValues) ?? this.loadStateValues(stateSaveKey);
            return new RowComponentHydrationBackend(
                hydrationContext,
                { ...screenContext, containingScreenRow: undefined },
                stateValues,
                stateSaveKey,
                tables
            );
        };

        const components: (WireComponent | null)[] = [];
        const componentStates: ComponentState[] = [];
        if (hydratedScreen !== undefined) {
            assert(hydratedScreen.componentStates.length === componentHydrators.length);
            assert(hydratedScreen.wireScreen.components.length === componentHydrators.length);

            const contextChanged = !areScreenContextsEqual(internalScreen.context, screenContext);

            for (const [hydratorWithID, cs, wc] of zip(
                componentHydrators,
                hydratedScreen.componentStates,
                hydratedScreen.wireScreen.components
            )) {
                assert(cs !== undefined);
                if (contextChanged || isComponentStateDirty(cs)) {
                    const [hydrator, componentID] = defined(hydratorWithID);
                    const hb = makeHydrationBackend(cs, componentID);
                    const component = hydrator(hb);
                    components.push(component ?? null);
                    componentStates.push(hb.subscribeComponent(this.onDataChange));

                    this.retireSubscriptionInfo(cs);
                } else {
                    components.push(defined(wc));
                    componentStates.push(keepSubscriptionInfo(hydrationContext, hydratedScreen, cs));
                }
            }
        } else {
            for (const [hydrator, componentID] of componentHydrators) {
                const hb = makeHydrationBackend(undefined, componentID);
                const component = hydrator(hb);
                components.push(component ?? null);
                componentStates.push(hb.subscribeComponent(this.onDataChange));
            }
            assert(components.length === componentStates.length);
        }

        let title: string;
        let titleSubscriptionInfo: SubscriptionInfo;
        if (hydratedScreen === undefined || isSubscriptionDirty(hydratedScreen.titleSubscriptionInfo)) {
            if (hydratedScreen !== undefined) {
                this.retireSubscriptionInfo(hydratedScreen.titleSubscriptionInfo);
            }
            const titleHB = makeHydrationBackend(undefined, undefined);
            title = asMaybeString(titleGetter(titleHB)) ?? defaultTitle ?? "";
            titleSubscriptionInfo = titleHB.subscribeComponent(this.onDataChange);
        } else {
            title = hydratedScreen.wireScreen.title;
            titleSubscriptionInfo = keepSubscriptionInfo(
                hydrationContext,
                hydratedScreen,
                hydratedScreen.titleSubscriptionInfo
            );
        }

        let closeAction: WireAction | undefined;
        if (hydrationContext.position !== PageScreenPosition.Main) {
            const { position } = hydrationContext;
            const hb = makeHydrationBackend(undefined, undefined);
            closeAction = registerActionRunner(hb, async ab => {
                ab.closeScreen(position);
                return true;
            });
        }

        return {
            hydratedScreen: {
                actions: hydrationContext.actions,
                onChangeData: hydrationContext.onChangeValueRows,
                wireScreen: {
                    title,
                    components,
                    closeAction,
                },
                screenContextSubscriptionInfo,
                componentStates: componentStates,
                titleSubscriptionInfo,
            },
            context: screenContext,
        };
    }

    private inflateArrayScreen(screen: ArrayScreenDescription): InflatedArrayScreen | undefined {
        const handler = handlerForArrayScreenFormat(screen.format);
        if (handler === undefined) return undefined;

        if (!isSingleRelationType(screen.type)) return undefined;
        if (screen.fetchesData !== true) return undefined;

        const table = this.context.findTable(screen.type);
        if (table === undefined) return undefined;

        this.dataStore.fetchTableRows(getTableName(table), false);

        const { computationModel } = this;
        const ib = this.makeInflationBackend(
            { ...makeInputOutputTables(table), containingScreen: undefined },
            undefined
        );
        if (computationModel === undefined || ib === undefined) return undefined;

        const contentHydrator = handler.inflateContent(ib, screen);
        if (contentHydrator === undefined) return undefined;

        return {
            table,
            contentHydrator,
            getContextTable: (hydrationContext, oldSubscriptionInfo) => {
                if (oldSubscriptionInfo !== undefined) {
                    this.retireSubscriptionInfo(oldSubscriptionInfo);
                }

                const tableName = getTableName(table);

                const tableData = computationModel.getBaseDataForTable(tableName);
                if (tableData === undefined) return undefined;
                hydrationContext.fetchTableData(tableName);

                // TODO: Subscribe to whatever we're sorting/filtering by
                // once we do that and once we support array screens.
                const rowIndexRootPath = this.getRowIndexRootPath(tableName);
                const subscriptionHandler = new TableSubscriptionHandler(
                    {
                        globalKeys: new Set(),
                        columnsInDirectRows: new Map([[rowIndexRootPath.rest.key, new Set([rowIndexColumnName])]]),
                        columnsInIndirectRows: new Map(),
                    },
                    this.onDataChange
                );

                const subscriptionPath = addSubscriptionHandler(
                    computationModel.ns,
                    "arrayScreenContext",
                    subscriptionHandler
                );
                const subscriptionInfo: SubscriptionInfo = {
                    handlerAndPath: { handler: subscriptionHandler, path: subscriptionPath },
                    tokens: new Set(),
                    subsidiary: undefined,
                };

                return [tableData, subscriptionInfo];
            },
        };
    }

    private hydrateArrayScreen(
        screen: ArrayScreenDescription,
        hydrationContext: ScreenHydrationContext
    ): ScreenHydrationResult | undefined {
        if (this.computationModel === undefined) return undefined;

        const { internalScreen } = hydrationContext;
        const { hydratedScreen, screenName } = internalScreen;

        let inflatedScreen = this.inflatedArrayScreens.get(screenName);
        if (inflatedScreen === undefined) {
            inflatedScreen = this.inflateArrayScreen(screen);
            if (inflatedScreen === undefined) return undefined;
            this.inflatedArrayScreens.set(screenName, inflatedScreen);
        }

        const { table, contentHydrator, getContextTable } = inflatedScreen;

        const oldScreenContextSubscriptionInfo = hydratedScreen?.screenContextSubscriptionInfo;
        const maybeScreenContext = getContextTable(hydrationContext, oldScreenContextSubscriptionInfo);
        if (maybeScreenContext === undefined) return undefined;
        const [tableData, screenContextSubscriptionInfo] = maybeScreenContext;

        const makeHydrationBackend = () =>
            new TableComponentHydrationBackend(
                hydrationContext,
                Array.from(tableData.values()),
                makeInputOutputTables(table)
            );

        const oldContentState = hydratedScreen?.componentStates[0];
        let contentComponentState: ComponentState;
        let component: WireComponent | undefined;
        if (
            oldContentState === undefined ||
            isComponentStateDirty(oldContentState) ||
            oldScreenContextSubscriptionInfo === undefined ||
            isSubscriptionDirty(oldScreenContextSubscriptionInfo)
        ) {
            const contentHydrationBackend = makeHydrationBackend();
            component = contentHydrator(contentHydrationBackend, undefined);
            contentComponentState = contentHydrationBackend.subscribe(this.onDataChange);
        } else {
            assert(hydratedScreen !== undefined);
            component = nullToUndefined(hydratedScreen.wireScreen.components[0]);
            contentComponentState = keepSubscriptionInfo(hydrationContext, defined(hydratedScreen), oldContentState);
        }

        const wireScreen: WireScreen = {
            components: definedMap(component, c => [c]) ?? [],
            // TODO: support title, once we support array screens
            title: internalScreen.defaultTitle ?? "",
        };

        return {
            hydratedScreen: {
                actions: hydrationContext.actions,
                onChangeData: hydrationContext.onChangeValueRows,
                wireScreen,
                screenContextSubscriptionInfo,
                componentStates: [contentComponentState],
                titleSubscriptionInfo: {
                    handlerAndPath: undefined,
                    tokens: new Set(),
                    subsidiary: undefined,
                },
            },
            // TODO: We need a proper context, once we support array screens.
            context: emptyInternalScreenContext,
        };
    }

    private hydrateScreen(hydrationContext: ScreenHydrationContext): ScreenHydrationResult | undefined {
        const { screenName } = hydrationContext.internalScreen;
        const screen = this.appDescription.screenDescriptions[screenName] as ScreenDescription | undefined;

        if (
            screen?.kind === ScreenDescriptionKind.Class ||
            (screen === undefined && screenName === userProfileScreenName)
        ) {
            return this.hydrateClassScreen(screen, hydrationContext);
        } else if (screen?.kind === ScreenDescriptionKind.Array) {
            return this.hydrateArrayScreen(screen, hydrationContext);
        } else if (screen?.kind === ScreenDescriptionKind.Chat || screen?.kind === ScreenDescriptionKind.ShoppingCart) {
            return undefined;
        } else {
            assert(screen === undefined);
            return undefined;

            // TS 4.1 doesn't support this, 4.3 does:
            //
            // if (screen === undefined) return undefined;
            // return assertNever(screen);
        }
    }

    private inflateUserProfile(): InflatedUserProfile | undefined {
        if (this.inflatedUserProfile !== undefined) return this.inflatedUserProfile;

        if (this.computationModel === undefined) return undefined;

        const ib = this.makeInflationBackend(dummyUserProfileTables, undefined);
        if (ib === undefined) return undefined;

        const info = getUserProfileTableInfo(this.appDescription);

        const verifiedEmailPath = this.computationModel.getVerifiedEmailAddressPath();
        let inflated: InflatedUserProfile = {
            emailValueGetter: hb => hb.getGlobalValue(undefined, verifiedEmailPath),
            nameValueGetter: undefined,
        };

        if (info !== undefined) {
            const [nameGetter, nameType] = ib.getValueGetterForColumn(
                { kind: SourceColumnKind.UserProfile, name: [info.nameColumnName] },
                false,
                true
            );
            if (isStringTypeKind(nameType.kind)) {
                inflated = { ...inflated, nameValueGetter: nameGetter };
            }
        }

        this.inflatedUserProfile = inflated;
        return inflated;
    }

    private hydrateUserProfile(
        tablesToFetch: ArraySet<TableName>
    ): [WireUserProfile | undefined, SubscriptionInfo | undefined] {
        const inflated = this.inflateUserProfile();
        if (inflated === undefined) return [undefined, undefined];

        const vp = new RowHydrationValueProvider(
            {
                namespace: this.computationModel?.ns,
                fetchTableData: tn => tablesToFetch.add(tn),
                actions: new Map(),
            },
            undefined,
            undefined,
            undefined
        );

        let userProfile: WireUserProfile | undefined;
        const email = asMaybeString(inflated.emailValueGetter(vp));
        if (email !== undefined) {
            const name = asMaybeString(inflated.nameValueGetter?.(vp));
            userProfile = {
                userProfileButton: {
                    signedInUserEmail: email,
                    signedInUserName: name,
                    userProfileAction: this.showUserProfileAction,
                },
            };
        } else {
            userProfile = {
                signInAction: this.isBuilder ? this.showUserProfileAction : this.signInAction,
            };
        }

        return [userProfile, vp.subscribe(this.onDataChange)];
    }

    // This makes sure that all the dirt makes its way up to the subscription
    // handlers.
    private recomputeSubscriptionHandlers(): void {
        const process = (si: SubscriptionInfo | undefined) => {
            if (si?.handlerAndPath !== undefined) {
                this.computationModel?.ns.get(si.handlerAndPath.path);
            }
            if (si?.subsidiary !== undefined) {
                process(si.subsidiary);
            }
        };

        const processScreen = (screen: InternalScreen | undefined) => {
            if (screen?.hydratedScreen === undefined) return;

            process(screen.hydratedScreen.screenContextSubscriptionInfo);
            for (const si of screen.hydratedScreen.componentStates) {
                process(si);
            }
        };

        processScreen(this.internalNavigationModel?.screen);
        processScreen(this.internalNavigationModel?.sideScreen);
        processScreen(this.internalNavigationModel?.modal);
        for (const tab of this.internalNavigationModel?.tabs ?? []) {
            process(tab.visibilitySubscriptionInfo);
        }

        process(this.internalNavigationModel?.userProfileSubscriptionInfo);
    }

    private getParsedScreenDescription(parsedScreen: ParsedScreen): ClassScreenDescription | undefined {
        const screen = this.appDescription.screenDescriptions[parsedScreen.screenName];
        if (screen?.kind !== ScreenDescriptionKind.Class) return undefined;
        return screen;
    }

    // `false` means the screen fetches its own row
    private getRowForParsedScreen(parsedScreen: ParsedScreen): Row | false | undefined {
        const screen = this.getParsedScreenDescription(parsedScreen);
        if (screen === undefined) return undefined;

        if (parsedScreen.rowID === undefined) {
            if (screen.fetchesData === true) {
                return false;
            } else {
                return undefined;
            }
        }

        const table = this.computationModel?.getBaseDataForTable(getTableName(getTableRefTableName(screen.type)));
        return table?.get(parsedScreen.rowID);
    }

    private makeInternalScreen(parsedScreen: ParsedScreen): InternalScreen {
        const { screenName } = parsedScreen;
        const screen = this.getParsedScreenDescription(parsedScreen);

        const tab = this.findTab(screenName)?.[0];

        let context: InternalScreenContext | undefined;
        const row = this.getRowForParsedScreen(parsedScreen);
        if (parsedScreen.rowID !== undefined) {
            if (row !== undefined && row !== false) {
                if (screen !== undefined && isFormScreen(screen)) {
                    const outputTableName = getTableRefTableName(screen.formType);
                    const outputRow = this.addEmptyRow(outputTableName);
                    context = { inputRow: row, outputRow };
                } else if (screen !== undefined && isEditClassScreenName(screenName)) {
                    const outputTableName = getTableRefTableName(screen.type);
                    const outputRow = this.copyRowWithNewRowID(outputTableName, {
                        ...row,
                        $rowID: makeRowID(),
                        $isVisible: false,
                    });
                    context = { inputRow: row, outputRow };
                } else if (screen !== undefined && isAddClassScreenName(screenName)) {
                    const outputTableName = getTableRefTableName(screen.type);
                    const outputRow = this.addEmptyRow(outputTableName);
                    context = { inputRow: undefined, outputRow };
                } else {
                    context = { inputRow: row, outputRow: undefined };
                }
            }
        }

        return {
            screenName,
            defaultTitle: tab?.title,
            context: context ?? emptyInternalScreenContext,
            hydratedScreen: undefined,
        };
    }

    private updateInternalScreen(
        parsedScreen: ParsedScreen,
        internalScreen: InternalScreen | undefined
    ): InternalScreen {
        const retireAndMakeDefault = () => {
            if (internalScreen !== undefined) {
                this.retireScreen(internalScreen);
            }
            return this.makeInternalScreen(parsedScreen);
        };

        if (internalScreen === undefined || parsedScreen.screenName !== internalScreen.screenName) {
            return retireAndMakeDefault();
        }

        const row = this.getRowForParsedScreen(parsedScreen);
        if (row !== false && row !== internalScreen.context.inputRow) {
            return retireAndMakeDefault();
        }

        return internalScreen;
    }

    private getParsedScreenAtPosition(position: PageScreenPosition): ParsedScreen | undefined {
        switch (position) {
            case PageScreenPosition.Main:
                return this.parsedPath?.screen;
            case PageScreenPosition.Side:
                return this.parsedPath?.sideScreen;
            case PageScreenPosition.Modal:
                return this.parsedPath?.modal;
            default:
                return assertNever(position);
        }
    }

    private rebuildScreen(
        internalScreen: InternalScreen,
        position: PageScreenPosition,
        tablesToFetch: ArraySet<TableName>,
        internalTabs: readonly InternalTab[] | undefined
    ): InternalScreen {
        const visibleTabScreenNames = new Set(
            mapFilterUndefined(internalTabs ?? [], t => (t.isVisible ? t.tabScreenName : undefined))
        );

        const hydrationContext: ScreenHydrationContext = {
            namespace: this.computationModel?.ns,
            internalScreen,
            fetchTableData: tn => tablesToFetch.add(tn),
            actions: new Map(),
            onChangeValueRows: new Map(),
            position,
            depth: this.getParsedScreenAtPosition(position)?.depth ?? 0,
            visibleTabScreenNames,
            verifiedEmailAddressPath: this.computationModel?.getVerifiedEmailAddressPath(),
        };
        // `hydrateScreen` is responsible for retiring everything that needs
        // to be retired, unless it fails, in which case it really should
        // never have succeeded.
        const result = this.hydrateScreen(hydrationContext);
        if (result === undefined) {
            if (internalScreen.hydratedScreen !== undefined) {
                this.retireHydratedScreen(internalScreen.hydratedScreen);
            }
            return internalScreen;
        }
        return { ...internalScreen, context: result.context, hydratedScreen: result.hydratedScreen };
    }

    private computeTabVisibility(tablesToFetch: ArraySet<TableName>): readonly InternalTab[] | undefined {
        const appTabs = getAppTabs(this.appDescription);

        const hydrationContext: ValueProviderActionContext = {
            namespace: this.computationModel?.ns,
            fetchTableData: tn => tablesToFetch.add(tn),
            // TODO: There should maybe be a value provider that
            // doesn't do actions, for just evaluating predicates like
            // here.
            actions: new Map(),
        };

        const makeInternalTab = (
            visibilityPredicate: WirePredicate,
            tabScreenName: string | undefined
        ): InternalTab => {
            const hb = new RowHydrationValueProvider(hydrationContext, undefined, undefined, undefined);
            const isVisible = visibilityPredicate(hb);
            return {
                isVisible,
                visibilityPredicate: visibilityPredicate,
                visibilitySubscriptionInfo: hb.subscribe(this.onDataChange),
                tabScreenName: tabScreenName ?? "Missing tab screen name",
            };
        };

        if (this.internalNavigationModel?.tabs === undefined) {
            const userProfileTable = definedMap(this.context.userProfileTableInfo?.tableName, tn =>
                this.context.findTable(tn)
            );

            let ib: InflationBackend | undefined;
            if (userProfileTable !== undefined) {
                ib = this.makeInflationBackend(
                    { ...makeInputOutputTables(userProfileTable), containingScreen: undefined },
                    undefined
                );
            }

            return appTabs.map(tab => {
                const filters = filterType(
                    tab.visibilityFilters ?? [],
                    (t): t is FilterArrayTransform => t.kind === ArrayTransformKind.Filter
                );
                const predicate = inflateFilters(ib, filters);
                return makeInternalTab(predicate, getScreenProperty(tab.screenName));
            });
        } else {
            return this.internalNavigationModel.tabs.map(it => {
                if (!isSubscriptionDirty(it.visibilitySubscriptionInfo)) {
                    return it;
                }

                this.retireSubscriptionInfo(it.visibilitySubscriptionInfo);

                return makeInternalTab(it.visibilityPredicate, it.tabScreenName);
            });
        }
    }

    private updateParsedPathWithTabVisibility(internalTabs: readonly InternalTab[] | undefined): void {
        if (this.isBuilder) return;

        // If we couldn't evaluate internal tabs that means the app doesn't
        // have a user profile table or something else missing, so we just
        // ignore visibility.
        if (internalTabs === undefined || this.parsedPath === undefined) return;

        const appTabs = getAppTabs(this.appDescription);
        assert(internalTabs.length === appTabs.length);

        const invisibleRootScreens = new Set<string>();
        let defaultTab: TabDescription | undefined;
        let defaultTabScreenName: string | undefined;
        for (const [tab, internalTab] of zip(appTabs, internalTabs)) {
            const visible = internalTab?.isVisible !== false;

            if (visible) {
                if (defaultTab === undefined) {
                    defaultTab = tab;
                    defaultTabScreenName = getScreenProperty(tab?.screenName);
                }
            } else {
                invisibleRootScreens.add(defined(internalTab).tabScreenName);
            }
        }

        function process(screen: ParsedScreen | undefined): ParsedScreen | undefined {
            if (screen === undefined) return undefined;
            if (invisibleRootScreens.has(screen.screenName)) return undefined;
            return screen;
        }

        let screen = process(this.parsedPath.screen);
        if (screen === undefined) {
            // If there's no default tab then no tab is visible, so we're
            // resetting the parsed path.
            //
            // FIXME: Allow for restoring the parsed path when tabs become
            // visible again.
            if (defaultTab === undefined || defaultTabScreenName === undefined) {
                this.parsedPath = undefined;
                return;
            }

            screen = {
                screenName: defaultTabScreenName,
                rowID: undefined,
                title: defaultTab.title,
                depth: 0,
            };
        }

        let tabScreenName = this.parsedPath.tabScreenName;
        if (invisibleRootScreens.has(tabScreenName)) {
            if (defaultTabScreenName === undefined) {
                this.parsedPath = undefined;
                return;
            }
            tabScreenName = defaultTabScreenName;
        }

        const sideScreen = process(this.parsedPath.sideScreen);
        const modal = process(this.parsedPath.modal);

        this.parsedPath = {
            screen,
            sideScreen,
            modal,
            tabScreenName,
        };
    }

    private rebuildNavigationModel(): void {
        if (this.internalNavigationModel !== undefined) {
            assert(this.parsedPath !== undefined);
        }
        if (this.parsedPath === undefined) {
            assert(this.internalNavigationModel === undefined);
            return;
        }

        this.recomputeSubscriptionHandlers();

        const tablesToFetch = new ArraySet<TableName>(areTableNamesEqual);

        // 1. Compute tab visibility.
        const internalTabs = this.computeTabVisibility(tablesToFetch);

        // 2. Update the parsed path to not show invisible tab root screens,
        //    and to not have an invisible tab selected.
        this.updateParsedPathWithTabVisibility(internalTabs);

        // 3. Update the internal navigation model to reflect the parsed path.
        if (this.internalNavigationModel === undefined) {
            const screen = this.makeInternalScreen(this.parsedPath.screen);
            const sideScreen = definedMap(this.parsedPath.sideScreen, p => this.makeInternalScreen(p));
            const modal = definedMap(this.parsedPath.modal, p => this.makeInternalScreen(p));
            this.internalNavigationModel = {
                screen,
                sideScreen: sideScreen,
                modal,
                tabs: undefined,
                userProfile: {
                    userProfileButton: undefined,
                    signInAction: undefined,
                },
                userProfileSubscriptionInfo: undefined,
            };
        } else {
            this.internalNavigationModel = {
                screen: this.updateInternalScreen(this.parsedPath.screen, this.internalNavigationModel.screen),
                sideScreen: definedMap(this.parsedPath.sideScreen, m =>
                    this.updateInternalScreen(m, this.internalNavigationModel?.sideScreen)
                ),
                modal: definedMap(this.parsedPath.modal, m =>
                    this.updateInternalScreen(m, this.internalNavigationModel?.modal)
                ),
                tabs: this.internalNavigationModel.tabs,
                userProfile: this.internalNavigationModel.userProfile,
                userProfileSubscriptionInfo: this.internalNavigationModel.userProfileSubscriptionInfo,
            };
        }

        const wireTabs: WireTab[] = [];
        const wireFlyoutItems: WireTab[] = [];

        const appTabs = getAppTabs(this.appDescription);

        for (const [appTab, internalTab] of zip(appTabs, internalTabs ?? [])) {
            assert(appTab !== undefined);

            if (appTab.hidden || internalTab?.isVisible === false) continue;

            const tabScreenName = getScreenProperty(appTab.screenName);
            if (tabScreenName === undefined) continue;

            const wireTab: WireTab = {
                title: appTab.title ?? "",
                icon: appTab.icon,
                isActive: tabScreenName === this.parsedPath.tabScreenName,
                action: this.navigateToTabActions.get(tabScreenName),
            };

            if (appTab.inFlyout === true) {
                wireFlyoutItems.push(wireTab);
            } else {
                wireTabs.push(wireTab);
            }
        }

        // 4. Recompute screens.
        let userProfile: WireUserProfile | undefined;
        let userProfileSubscriptionInfo: SubscriptionInfo | undefined;
        if (
            this.internalNavigationModel.userProfileSubscriptionInfo === undefined ||
            isSubscriptionDirty(this.internalNavigationModel.userProfileSubscriptionInfo)
        ) {
            if (this.internalNavigationModel.userProfileSubscriptionInfo !== undefined) {
                this.retireSubscriptionInfo(this.internalNavigationModel.userProfileSubscriptionInfo);
            }

            const hydrated = this.hydrateUserProfile(tablesToFetch);
            userProfile = hydrated[0];
            userProfileSubscriptionInfo = hydrated[1];
        } else {
            userProfile = this.internalNavigationModel.userProfile;
            userProfileSubscriptionInfo = this.internalNavigationModel.userProfileSubscriptionInfo;
        }

        this.internalNavigationModel = {
            screen: this.rebuildScreen(
                this.internalNavigationModel.screen,
                PageScreenPosition.Main,
                tablesToFetch,
                internalTabs
            ),
            sideScreen: definedMap(this.internalNavigationModel.sideScreen, m =>
                this.rebuildScreen(m, PageScreenPosition.Side, tablesToFetch, internalTabs)
            ),
            modal: definedMap(this.internalNavigationModel.modal, m =>
                this.rebuildScreen(m, PageScreenPosition.Modal, tablesToFetch, internalTabs)
            ),
            tabs: internalTabs,
            userProfile,
            userProfileSubscriptionInfo,
        };

        const navModel: WireNavigationModel = {
            urlPath: this.unparseParsedPath(this.parsedPath),
            tabs: wireTabs,
            flyoutItems: wireFlyoutItems,
            screen: this.internalNavigationModel.screen.hydratedScreen?.wireScreen ?? emptyScreen,
            sideBar: this.internalNavigationModel.sideScreen?.hydratedScreen?.wireScreen,
            modal: this.internalNavigationModel.modal?.hydratedScreen?.wireScreen,
            userProfileButton: this.internalNavigationModel.userProfile?.userProfileButton,
            signInAction: this.internalNavigationModel.userProfile?.signInAction,
            // FIXME: This has to come from the eminence, which we'll have to
            // add to the published app.
            removeBranding: false,
        };
        this.navigationModelWatchable.current = navModel;
        this.metadataWatchable.current = this.makeMetadata();

        for (const tableName of tablesToFetch.values()) {
            this.dataStore.fetchTableRows(tableName, false);
        }
    }

    private recompute(): void {
        if (this.state === State.Retired) return;

        logInfo("recomputing", this.id);

        assert(this.state === State.Requested);
        this.state = State.Recomputing;
        try {
            this.rebuildNavigationModel();
        } finally {
            if ((this.state as State) === State.FollowUpRequested) {
                this.state = State.Clean;
                this.onDataChange();
                return;
            } else {
                assert(this.state === State.Recomputing);
                this.state = State.Clean;
            }
        }
    }

    private pushScreen(
        parsedScreen: Omit<ParsedScreen, "depth">,
        // `source === undefined` means go to the tab root.  In that case
        // `target` must be `PageScreenPosition.Main`.
        source: PageScreenPosition | undefined,
        target: PageScreenTarget
    ): void {
        const { parsedPath } = this;
        if (parsedPath === undefined) return;

        let targetPosition: PageScreenPosition;
        if (target === "current") {
            targetPosition = source ?? PageScreenPosition.Main;
        } else {
            targetPosition = target;
        }

        let update: Partial<ParsedPath>;
        if (source !== undefined) {
            update = {};
        } else {
            assert(targetPosition === PageScreenPosition.Main);
            update = { screen: undefined, sideScreen: undefined, modal: undefined };
        }

        let previousScreen: ParsedScreen | undefined;
        if (targetPosition === PageScreenPosition.Main) {
            previousScreen = this.parsedPath?.screen;
        } else if (targetPosition === PageScreenPosition.Side) {
            previousScreen = this.parsedPath?.sideScreen;
        } else if (targetPosition === PageScreenPosition.Modal) {
            previousScreen = this.parsedPath?.modal;
        } else {
            return assertNever(targetPosition);
        }

        const screenWithDepth: ParsedScreen = {
            ...parsedScreen,
            depth: definedMap(previousScreen, s => s.depth + 1) ?? 0,
        };

        if (targetPosition === PageScreenPosition.Main) {
            update = { ...update, screen: screenWithDepth };
        } else if (targetPosition === PageScreenPosition.Side) {
            update = { ...update, sideScreen: screenWithDepth };
        } else if (targetPosition === PageScreenPosition.Modal) {
            update = { ...update, modal: screenWithDepth };
        } else {
            return assertNever(targetPosition);
        }
        this.parsedPath = { ...parsedPath, ...update };
    }

    private runPushFreeScreen(
        screenName: string,
        title: string | undefined,
        source: PageScreenPosition | undefined,
        target: PageScreenTarget,
        row: Row | undefined
    ): void {
        this.pushScreen({ screenName, rowID: row?.$rowID, title }, source, target);
    }

    private runPushDefaultClassScreen(
        tableName: TableName,
        row: Row,
        title: string | undefined,
        source: PageScreenPosition | undefined,
        target: PageScreenTarget
    ): void {
        this.pushScreen({ screenName: classScreenName(tableName), rowID: row.$rowID, title }, source, target);
    }

    private runPushFormScreen(
        screenName: string,
        title: string | undefined,
        row: Row | undefined,
        source: PageScreenPosition | undefined,
        target: PageScreenTarget
    ): void {
        this.pushScreen({ screenName, rowID: row?.$rowID, title }, source, target);
    }

    private runPushDefaultEditScreen(
        tableName: TableName,
        title: string | undefined,
        row: Row,
        source: PageScreenPosition | undefined,
        target: PageScreenTarget
    ): void {
        this.pushScreen({ screenName: editClassScreenName(tableName), rowID: row.$rowID, title }, source, target);
    }

    private runPushUserProfileScreen(
        title: string | undefined,
        source: PageScreenPosition,
        target: PageScreenTarget
    ): void {
        this.pushScreen({ screenName: userProfileScreenName, rowID: undefined, title }, source, target);
    }

    private runCloseScreen(position: PageScreenPosition.Side | PageScreenPosition.Modal): void {
        if (this.parsedPath === undefined) return;

        if (position === PageScreenPosition.Side) {
            this.parsedPath = { ...this.parsedPath, sideScreen: undefined };
        } else if (position === PageScreenPosition.Modal) {
            this.parsedPath = { ...this.parsedPath, modal: undefined };
        } else {
            return assertNever(position);
        }
    }

    private runNavigateToTab(tabScreenNameOrLogicalTabIndex: string | number, force: boolean): void {
        if (tabScreenNameOrLogicalTabIndex === userProfileScreenName) {
            // We special-case the user profile screen here to push it as a
            // modal.  If we ever want to have the user profile screen as a
            // tab then this will have to change and we'll need to explicitly
            // push it as a modal if that's what we want.
            this.runPushUserProfileScreen(undefined, PageScreenPosition.Main, PageScreenPosition.Modal);
        } else {
            const tabs = getAppTabs(this.appDescription);
            let tab: TabDescription | undefined;
            if (typeof tabScreenNameOrLogicalTabIndex === "string") {
                const result = this.findTab(tabScreenNameOrLogicalTabIndex);
                if (result === undefined) return;
                tab = result[0];
                tabScreenNameOrLogicalTabIndex = result[1];
            } else {
                tab = tabs[tabScreenNameOrLogicalTabIndex];
            }

            const internalTab = this.internalNavigationModel?.tabs?.[tabScreenNameOrLogicalTabIndex];
            if (internalTab === undefined) return;
            if (!internalTab.isVisible && !force) return;

            const { tabScreenName } = internalTab;

            this.parsedPath = {
                tabScreenName,
                screen: { screenName: tabScreenName, rowID: undefined, title: defined(tab).title, depth: 0 },
                sideScreen: undefined,
                modal: undefined,
            };
        }
    }

    public setTab(tabScreenNameOrLogicalTabIndex: string | number, fromBuilder: boolean): void {
        this.runNavigateToTab(tabScreenNameOrLogicalTabIndex, fromBuilder);
        this.requestSyncRecompute();
    }

    public popToLogicalTab(): void {
        if (this.parsedPath === undefined) return;

        const [tab] = defined(this.findTab(this.parsedPath.tabScreenName));

        this.parsedPath = {
            tabScreenName: this.parsedPath.tabScreenName,
            screen: {
                screenName: this.parsedPath.tabScreenName,
                rowID: undefined,
                title: tab.title,
                depth: 0,
            },
            sideScreen: undefined,
            modal: undefined,
        };

        this.requestSyncRecompute();
    }

    private async runSetColumnsInRow(
        tableName: TableName,
        row: Row,
        updates: Record<string, GroundValue>
    ): Promise<boolean> {
        const table = this.context.findTable(tableName);
        if (table === undefined) return false;

        const rowIndex = getRowIndexForRow(table, row);
        if (rowIndex === undefined) return false;

        await this.dataStore.setColumnsInRow(
            getTableName(table),
            rowIndex,
            Data.fromJSON<AppOther, AppEnvironment>(updates),
            true,
            true,
            false
        );

        return true;
    }

    private async runDeleteRow(tableName: TableName, row: Row): Promise<boolean> {
        if (!row.$isVisible) return false;

        const table = this.context.findTable(tableName);
        if (table === undefined) return false;

        const rowIndex = getRowIndexForRow(table, row);
        if (rowIndex === undefined) return false;

        await this.dataStore.deleteRowAtIndex(tableName, rowIndex, true, false);

        return true;
    }

    private async runAddRow(tableName: TableName, r: Row): Promise<boolean> {
        const outputTable = this.context.findTable(tableName);
        if (outputTable === undefined) return false;

        assert(!r.$isVisible);

        const keeper = this.computationModel?.tableKeeperStore.getTableKeeperForTable(tableName);
        keeper?.deleteInvisibleRow(r.$rowID);

        const newRow: Record<string, GroundValue> = {};
        const columnNames = new Set<string>();
        for (const c of outputTable.columns) {
            if (isComputedColumn(c)) continue;
            if (!isPrimitiveType(c.type)) continue;

            const v = getRowColumn(r, c.name);
            if (v === undefined) continue;

            newRow[c.name] = v;
            columnNames.add(c.name);
        }
        if (outputTable.rowIDColumn !== undefined) {
            newRow[outputTable.rowIDColumn] = r.$rowID;
        }

        const result = await this.dataStore.addRowToTable(
            tableName,
            Data.fromJSON<AppOther, AppEnvironment>(newRow),
            columnNames,
            true,
            false
        );
        return result.didAdd;
    }

    private async runSaveEditScreenChanges(tableName: TableName, sc: InternalScreenContext): Promise<boolean> {
        assert(sc.inputRow !== undefined && sc.outputRow !== undefined);

        const table = this.context.findTable(tableName);
        if (table === undefined) return false;

        const updates: Record<string, GroundValue> = {};

        for (const column of table.columns) {
            if (isComputedColumn(column)) continue;
            if (!isPrimitiveType(column.type)) continue;
            if (column.name === table.rowIDColumn) continue;
            if (column.name === "$rowID") continue;

            const inputValue = getRowColumn(sc.inputRow, column.name);
            const outputValue = getRowColumn(sc.outputRow, column.name);
            if (inputValue === outputValue) continue;

            updates[column.name] = outputValue;
        }

        return await this.runSetColumnsInRow(tableName, sc.inputRow, updates);
    }

    private copyRowWithNewRowID(tableName: TableName, row: Row): Row | undefined {
        const outputTable = this.context.findTable(tableName);
        if (outputTable === undefined) return undefined;

        const copy: Row = { ...row, $rowID: makeRowID() };
        if (outputTable.rowIDColumn !== undefined) {
            copy[outputTable.rowIDColumn] = copy.$rowID;
        }

        const keeper = this.computationModel?.tableKeeperStore.getTableKeeperForTable(tableName);
        keeper?.addInvisibleRow(copy);

        return copy;
    }

    private addEmptyRow(tableName: TableName): Row | undefined {
        return this.copyRowWithNewRowID(tableName, { $rowID: "", $isVisible: false });
    }

    private setColumnsInInvisibleRow(tableName: TableName, row: Row, update: Record<string, GroundValue>): void {
        const keeper = this.computationModel?.tableKeeperStore.getTableKeeperForTable(tableName);
        if (keeper === undefined) return;

        // TODO: We're kinda abusing this API.  I'm not sure it's
        // meant to let you update invisible rows without bothering
        // the data store.
        keeper.updateRowWithPartialData({ keyColumnName: "$rowID", keyColumnValue: row.$rowID }, update);
    }

    private requestSyncRecompute(): void {
        assert(this.state !== State.Recomputing && this.state !== State.Retired);
        if (this.state === State.Requested || this.state === State.FollowUpRequested) return;
        this.state = State.Requested;
        this.recompute();
    }

    private makeActionBackend(
        rowScreenContext: InternalRowContext | undefined,
        source: PageScreenPosition | undefined
    ): ActionBackendWithHydrationContext | undefined {
        if (this.computationModel === undefined) return undefined;

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const backend = this;

        class ActionBackend extends RowHydrationValueProvider<ValueProviderActionContext> implements WireActionBackend {
            constructor(public readonly hydrationContext: ValueProviderActionContext) {
                super(hydrationContext, rowScreenContext, undefined, undefined);
            }

            public get appID(): string {
                return backend.appID;
            }

            public get actionCallbacks(): WireActionCallbacks {
                return backend.callbacks;
            }

            public getState<T>(
                _name: string,
                _validate: (v: unknown) => v is T,
                _defaultValue: T
            ): WireEditableValue<T> {
                return panic("State is not supported in actions");
            }

            public pushFreeScreen(
                screenName: string,
                title: string | undefined,
                target: PageScreenTarget,
                row: Row | undefined
            ): void {
                backend.runPushFreeScreen(screenName, title, source, target, row);
            }

            public pushDefaultClassScreen(
                tableName: TableName,
                r: Row,
                title: string | undefined,
                target: PageScreenTarget
            ): void {
                backend.runPushDefaultClassScreen(tableName, r, title, source, target);
            }

            public pushDefaultEditScreen(
                tableName: TableName,
                title: string | undefined,
                r: Row,
                target: PageScreenTarget
            ): void {
                backend.runPushDefaultEditScreen(tableName, title, r, source, target);
            }

            public pushFormScreen(
                screenName: string,
                title: string | undefined,
                r: Row,
                target: PageScreenTarget
            ): void {
                backend.runPushFormScreen(screenName, title, r, source, target);
            }

            public pushUserProfileScreen(title: string | undefined, target: PageScreenTarget): void {
                // This will be called from the user profile icon in the top
                // right, in which case the `source` will be `undefined`, so
                // we substitute `Main`.
                backend.runPushUserProfileScreen(title, source ?? PageScreenPosition.Main, target);
            }

            public closeScreen(position: PageScreenPosition.Side | PageScreenPosition.Modal): void {
                backend.runCloseScreen(position);
            }

            public navigateToTab(tabScreenName: string): void {
                backend.runNavigateToTab(tabScreenName, false);
            }

            public async addRow(tableName: TableName, r: Row): Promise<boolean> {
                return await backend.runAddRow(tableName, r);
            }

            public async setColumnsInRow(
                tableName: TableName,
                r: Row,
                updates: Record<string, GroundValue>
            ): Promise<boolean> {
                return await backend.runSetColumnsInRow(tableName, r, updates);
            }

            public async deleteRow(tableName: TableName, r: Row): Promise<boolean> {
                return await backend.runDeleteRow(tableName, r);
            }

            public async saveEditScreenChanges(tableName: TableName, sc: InternalScreenContext): Promise<boolean> {
                return await backend.runSaveEditScreenChanges(tableName, sc);
            }
        }

        const context: ValueProviderActionContext = {
            namespace: this.computationModel.ns,
            fetchTableData: () => undefined,
            // This isn't actually needed, but our types demand it
            actions: new Map(),
        };

        return new ActionBackend(context);
    }

    private async runActionWithRunnerAndRequestRecompute(
        ab: ActionBackendWithHydrationContext,
        runner: WireActionRunner
    ): Promise<void> {
        await runner(ab);
        assert(ab.hydrationContext.actions.size === 0);
        this.requestSyncRecompute();
    }

    public runAction(token: string, _handled: boolean): void {
        assert(this.state !== State.Recomputing && this.state !== State.Retired);

        if (this.internalNavigationModel === undefined) return;

        let action = this.internalNavigationModel.screen.hydratedScreen?.actions.get(token);
        let source: PageScreenPosition | undefined = PageScreenPosition.Main;
        if (action === undefined) {
            action = this.internalNavigationModel.sideScreen?.hydratedScreen?.actions.get(token);
            source = PageScreenPosition.Side;
        }
        if (action === undefined) {
            action = this.internalNavigationModel.modal?.hydratedScreen?.actions.get(token);
            source = PageScreenPosition.Modal;
        }
        if (action === undefined) {
            action = this.globalActions.get(token);
            source = undefined;
        }
        if (action === undefined) return;

        const [runner, row] = action;

        const ab = this.makeActionBackend(row, source);
        if (ab === undefined) return;

        void this.runActionWithRunnerAndRequestRecompute(ab, runner);
    }

    private async runValueChanged(entry: OnChangeData, value: PrimitiveValue): Promise<void> {
        assert(this.state !== State.Recomputing && this.state !== State.Retired);

        if (entry.kind === "column") {
            const { tableName, columnName, row } = entry;

            const update = { [columnName]: value };

            if (row.$isVisible) {
                await this.runSetColumnsInRow(tableName, row, update);
            } else {
                this.setColumnsInInvisibleRow(tableName, row, update);
            }
        } else {
            return assertNever(entry.kind);
        }
    }

    public valueChanged(token: string, value: PrimitiveValue): void {
        if (this.internalNavigationModel === undefined) return;

        const processScreen = (hydratedScreen: HydratedScreen | undefined): boolean => {
            if (hydratedScreen === undefined) return false;

            const entry = hydratedScreen.onChangeData.get(token);
            if (entry !== undefined) {
                void this.runValueChanged(entry, value);
                return true;
            }

            for (const cs of hydratedScreen.componentStates ?? []) {
                for (const [k, [, t, c]] of cs.stateValues) {
                    if (token === t) {
                        cs.stateValues.set(k, [c(value), t, c]);
                        cs.stateChanged = true;
                        this.saveStateValues(cs.stateSaveKey, cs.stateValues);
                        return true;
                    }
                }
            }

            return false;
        };

        if (
            processScreen(this.internalNavigationModel.screen.hydratedScreen) ||
            processScreen(this.internalNavigationModel.sideScreen?.hydratedScreen) ||
            processScreen(this.internalNavigationModel.modal?.hydratedScreen)
        ) {
            this.requestSyncRecompute();
        }
    }

    public urlPathChanged(urlPath: string): void {
        if (this.parsedPath !== undefined && this.unparseParsedPath(this.parsedPath) === normalizeURLPath(urlPath)) {
            return;
        }

        const parsed = this.parseURLPath(urlPath);
        if (parsed === undefined) return;

        this.parsedPath = parsed;
        this.requestSyncRecompute();
    }

    private readonly onComputationModelChange = (): void => {
        if (this.state === State.FollowUpRequested) {
            return;
        }
        if (this.state === State.Recomputing) {
            this.state = State.FollowUpRequested;
            return;
        }
        assert(this.state === State.Clean || this.state === State.Requested);

        this.internalNavigationModel = undefined;
        this.inflatedClassScreens.clear();
        this.inflatedArrayScreens.clear();
        this.globalActions.clear();
        this.navigateToTabActions.clear();

        this.onDataChange();
    };

    private readonly onDataChange = (): void => {
        if (this.state !== State.Clean) return;
        this.state = State.Requested;

        logInfo("onDataChange", this.id);

        // It would be nice if we didn't have to do this async.  Also, make
        // sure we're only doing this once per change.  Might have to modify
        // the namespace to only call it when it has finished recomputing.
        setTimeout(() => this.recompute(), 0);
    };
}
